import React, { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Slide } from "react-slideshow-image";
import {
  CalendarDays,
  ChevronDown,
  ChevronRight,
  CloudUpload,
  Flag,
  MapPin,
  Medal,
  ScanLine,
  Search,
  Shield,
  Smartphone,
  Trophy,
  Users,
  Video,
  Download,
  X,
} from "lucide-react";
import "react-slideshow-image/dist/styles.css";
import ClubTournamentBoard from "./ClubTournamentBoard";
import { useBrand } from "../../context/BrandContext";
import { useChampionshipClipUploads } from "../../hooks/useChampionshipClipUploads";
import { clipUploadStatusText } from "../../utils/championshipClipUploader";
import {
  canManageChampionshipMatchPresence,
  isPlayerAccount,
  readActiveHomeRole,
  userHasAdminRole,
  userHasCommitteeRole,
  userHasModeratorRole,
} from "../../utils/homeRole";
import {
  federationClubFromUser,
  isBelgiumClub,
  isFranceFederationClub,
  isGermanyClub,
  isLuxembourgFederationClub,
} from "../../utils/clubCountryLanguage";
import {
  fetchLeagueFixtures,
  fetchLeagueFixtureOpponentPreview,
  fetchLeagueFixtureRsvps,
  fetchLeagueSeason,
  fetchMyLeagueRoster,
  fetchMyLeagueSchedule,
  listChampionshipMatches,
  readCompetitionClubId,
  listChampionshipNotesAboutPlayer,
  listCompetitions,
  openChampionshipMatch,
  openPlayerTournamentMatch,
  fetchChampionshipMatch,
  setChampionshipOpponentAvailability,
  setFlttTournamentRsvp,
  setLeagueFixtureRsvp,
  syncFlttMatchdayResults,
  fetchFlttTournaments,
  fetchFeltenCup,
  syncFeltenCup,
  openFeltenCupMatch,
} from "../../components/funtionsBD/competitionService";
import { useTourDemoActive } from "../../tour/useTourDemoActive";
import { demoCompetitionView } from "../../tour/tourDemoFixtures";
import { fixtureIsPast, matchPresenceLocked } from "../../utils/championshipMatchSheet";
import {
  cascadeMatchSquad,
  presenceOutsFromRsvps,
  rankedPool,
} from "../../utils/competitionLineup";
import { formatRankingPlace } from "../../utils/flttRankingDisplay";
import ClubLogoMark from "../../components/ClubLogoMark";
import "./CompetitionPage.css";

const ClubUsersModal = lazy(() => import("../../components/ClubUsersModal"));
const FlttStdTournamentResults = lazy(() => import("./FlttStdTournamentResults"));
const FlttFeltenCupPanel = lazy(() => import("./FlttFeltenCupPanel"));

function readUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "null");
  } catch {
    return null;
  }
}

function formatMatchDate(iso, locale) {
  if (!iso) return "";
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(locale, {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

function tournamentDateParts(iso, locale) {
  if (!iso) return null;
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return {
    day: d.toLocaleDateString(locale, { day: "numeric" }),
    month: d.toLocaleDateString(locale, { month: "short" }),
    weekday: d.toLocaleDateString(locale, { weekday: "short" }),
  };
}

function daysUntilMatch(iso) {
  if (!iso) return null;
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  return Math.round((d.getTime() - today.getTime()) / 86400000);
}

const MATCHES_TAB = "__matches__";
function officialScoreFromFixture(fixture) {
  if (!fixture?.official_played) return null;
  const home = Number(fixture.home_score);
  const away = Number(fixture.away_score);
  if (!Number.isFinite(home) || !Number.isFinite(away)) return null;
  return {
    home,
    away,
    status: "complete",
    complete: true,
    live: false,
    homeSets: 0,
    awaySets: 0,
    hasSets: false,
  };
}

function scoreForFixture(fixture, liveRow) {
  return matchScoreFromRow(liveRow) || officialScoreFromFixture(fixture);
}

function matchScoreFromRow(liveRow) {
  if (!liveRow) return null;
  const home = liveRow.home_team_score;
  const away = liveRow.away_team_score;
  if (home == null || away == null) return null;
  if (
    liveRow.status !== "complete" &&
    liveRow.status !== "live" &&
    Number(home) === 0 &&
    Number(away) === 0
  ) {
    return null;
  }
  return {
    home: Number(home),
    away: Number(away),
    status: liveRow.status || null,
    complete: liveRow.status === "complete",
    live: liveRow.status === "live",
    homeSets: Number(liveRow.home_sets_total),
    awaySets: Number(liveRow.away_sets_total),
    hasSets:
      Number(liveRow.home_sets_total) + Number(liveRow.away_sets_total) > 0,
  };
}

function relativeMatchLabel(iso, t) {
  const diff = daysUntilMatch(iso);
  if (diff == null) return "";
  if (diff === 0) return t("competitionToday", "Today");
  if (diff === 1) return t("competitionTomorrow", "Tomorrow");
  if (diff === -1) return t("competitionYesterday", "Yesterday");
  if (diff > 1 && diff < 21) return t("competitionInDays", "In {{n}} days", { n: diff });
  if (diff < -1 && diff > -21) return t("competitionDaysAgo", "{{n}} days ago", { n: Math.abs(diff) });
  return "";
}

function isHomeForTeam(fixture, team) {
  if (!fixture || !team) return false;
  return fixture.home?.league_team_id === team.league_team_id;
}

/** Win / loss / draw for the club team from a completed match score. */
function clubMatchOutcome(score, fixture, team) {
  if (!score?.complete || score.home == null || score.away == null || !team) {
    return null;
  }
  const home = isHomeForTeam(fixture, team);
  const ours = home ? Number(score.home) : Number(score.away);
  const theirs = home ? Number(score.away) : Number(score.home);
  if (!Number.isFinite(ours) || !Number.isFinite(theirs)) return null;
  if (ours > theirs) return "win";
  if (ours < theirs) return "loss";
  return "draw";
}

function opponentOf(fixture, team) {
  if (!fixture || !team) return null;
  return isHomeForTeam(fixture, team) ? fixture.away : fixture.home;
}

function opponentPreviewPlayerKey(player) {
  if (player?.user_id) return `u:${player.user_id}`;
  if (player?.player_id) return `p:${player.player_id}`;
  return `n:${String(player?.display_name || player?.name || "")
    .trim()
    .toLowerCase()}`;
}

function opponentPreviewFromSnapshot(snap) {
  const pool = snap?.opponent_pool || {};
  const side = pool.side || "away";
  const titular = (snap?.lineup?.[side] || []).filter(
    (s) => s.display_name || s.name || s.user_id || s.player_id
  );
  const keys = new Set(titular.map(opponentPreviewPlayerKey));
  const extras = (pool.players || []).filter(
    (p) => !keys.has(opponentPreviewPlayerKey(p))
  );
  return {
    ...pool,
    titular: titular.map((s) => ({
      ...s,
      expected: true,
      display_name: s.display_name || s.name || "",
    })),
    extras,
    players: pool.players || [],
    match_day: true,
    has_last_match: true,
  };
}

/** Official feuille lineup for one side (past / completed matches). */
function snapshotLineupSaved(snap) {
  if (!snap?.match) return false;
  if (snap.match.lineup_saved_at) return true;
  return ["home", "away"].some((side) =>
    (snap.lineup?.[side] || []).some((slot) => slot?.order_source === "sheet_photo")
  );
}

function officialLineupFromSnapshot(snap, side) {
  return (snap?.lineup?.[side] || [])
    .filter((s) => s.display_name || s.name || s.user_id || s.player_id)
    .map((s) => ({
      ...s,
      display_name: s.display_name || s.name || "",
      name: s.display_name || s.name || "",
    }));
}

/** Belgian sync stores uniqueness keys as CODE|divisionId — never show those. */
function isInternalClubKey(value) {
  return /^[A-Za-z0-9._-]+\|\d+$/.test(String(value || "").trim());
}

function stripTrailingTeamMark(value) {
  return String(value || "")
    .trim()
    .replace(/\s+[A-Za-z]$/, "")
    .trim();
}

function teamClubLabel(team) {
  const clubName = String(team?.club_name || "").trim();
  if (clubName) return clubName;
  const short = String(team?.club_short_name || "").trim();
  if (short && !isInternalClubKey(short)) return short;
  const display = String(team?.display_name || "").trim();
  const stripped = stripTrailingTeamMark(display);
  return stripped || display || "—";
}

function teamSideLabels(team, t) {
  const club = teamClubLabel(team);
  const display = String(team?.display_name || "").trim();
  const num = Number(team?.team_number);
  const teamText =
    Number.isFinite(num) && num > 0
      ? t("competitionTeamNumber", "Team {{n}}", { n: num })
      : display && fold(display) !== fold(club)
        ? display
        : null;
  return {
    club: club || display || "—",
    team: teamText,
  };
}

function OpponentPreviewBlock({
  preview,
  canSet,
  busy,
  onSetAvailability,
  t,
  embedded = false,
  showExtras = true,
  memoryNotes = [],
}) {
  const titular = preview?.titular || [];
  const extras = showExtras ? preview?.extras || [] : [];
  if (!titular.length && !extras.length) return null;

  const notesFor = (player) => {
    const name = String(player.display_name || player.name || "")
      .trim()
      .toLowerCase();
    return (memoryNotes || []).filter((n) => {
      if (n.about_user_id && player.user_id) {
        return String(n.about_user_id) === String(player.user_id);
      }
      if (n.about_player_id && player.player_id) {
        return String(n.about_player_id) === String(player.player_id);
      }
      return (
        name &&
        String(n.about_display_name || "")
          .trim()
          .toLowerCase() === name
      );
    });
  };

  const renderRow = (player, playing) => {
    const name = player.display_name || player.name || "—";
    const meta = [
      player.classement,
      formatRankingPlace(player.ranking_place ?? player.fltt_ranking_place),
      player.ranking_points != null ? `${player.ranking_points} pts` : null,
      player.in_last_match ? t("competitionLastMatch", "Last match") : null,
    ]
      .filter(Boolean)
      .join(" · ");
    const memories = notesFor(player);
    return (
      <li key={opponentPreviewPlayerKey(player)} className={playing ? "is-play" : "is-bench"}>
        <span className="comp-opp-preview__who">
          <strong>{name}</strong>
          {meta ? <small>{meta}</small> : null}
          {memories.length ? (
            <ul className="comp-opp-preview__memory">
              {memories.slice(0, 3).map((n) => (
                <li
                  key={n.note_id}
                  className={n.kind === "note" ? "is-note" : "is-feedback"}
                >
                  <em>
                    {n.kind === "note"
                      ? t("cmPrivateNote", "My note")
                      : n.author_name || t("cmFeedback", "Feedback")}
                    {n.source === "video"
                      ? ` · ${t("cmModeVideo", "Video")}`
                      : n.source === "audio" || n.source === "voice"
                        ? ` · ${t("cmModeAudio", "Audio")}`
                        : ""}
                  </em>
                  <span>{n.body}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </span>
        {canSet ? (
          <span className="comp-opp-preview__acts" role="group">
            {playing ? (
              <button
                type="button"
                className="is-out is-icon"
                disabled={busy}
                aria-label={t("competitionOpponentNotPresent", "Not present")}
                title={t(
                  "competitionOpponentNotPresent",
                  "Not present — next ranked player fills the seat"
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  onSetAvailability(player, "bench");
                }}
              >
                <X size={14} strokeWidth={2.4} aria-hidden />
              </button>
            ) : (
              <button
                type="button"
                className="is-in"
                disabled={busy}
                onClick={(e) => {
                  e.stopPropagation();
                  onSetAvailability(player, "play");
                }}
              >
                {t("competitionOpponentPlay", "Play")}
              </button>
            )}
          </span>
        ) : null}
      </li>
    );
  };

  const fromLastMatch =
    preview?.source === "last_match" ||
    (titular.length > 0 && titular.every((p) => p.in_last_match));
  const sourceLabel = fromLastMatch
    ? t("competitionLastMatch", "Last match")
    : null;

  return (
    <div
      className={`comp-opp-preview${embedded ? " is-embedded" : ""}${
        fromLastMatch ? " is-last-match" : ""
      }`}
    >
      {titular.length ? (
        <>
          <p className="comp-opp-preview__label">
            {embedded
              ? t("competitionLineup", "Line-up")
              : t("competitionOpponentTeam", "Opponent team")}
            {sourceLabel ? <span>{sourceLabel}</span> : null}
          </p>
          {fromLastMatch ? (
            <p className="comp-opp-preview__source">
              {t(
                "competitionOpponentFromLastMatch",
                "Proposed from their last match alignment."
              )}
            </p>
          ) : null}
          <ul className="comp-opp-preview__list">
            {titular.map((p) => renderRow(p, true))}
          </ul>
        </>
      ) : null}
      {extras.length ? (
        <>
          <p className="comp-opp-preview__label">
            {t("competitionOtherPotential", "Other potential players")}
          </p>
          <ul className="comp-opp-preview__list">{extras.map((p) => renderRow(p, false))}</ul>
        </>
      ) : null}
      {canSet && !embedded ? (
        <p className="comp-opp-preview__hint">
          {t(
            "competitionOpponentMatchDayHint",
            "On match day you can set who plays or sits until the official team is posted."
          )}
        </p>
      ) : null}
    </div>
  );
}

function initials(name) {
  return String(name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
}

function localIsoDate(value = new Date()) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function isOwnClubTeam(team, clubId) {
  return Boolean(clubId && team?.club_id && String(team.club_id) === String(clubId));
}

function ClubCrest({ team, size = "sm", ownClubId = null, onClick = null }) {
  const clubName = teamClubLabel(team);
  return (
    <ClubLogoMark
      clubImage={team?.club_image}
      clubName={clubName}
      size={size}
      className="comp-club-logo"
      useBrandFallback={isOwnClubTeam(team, ownClubId)}
      fallback="letter"
      title={clubName}
      onClick={onClick}
    />
  );
}

function clubCardPayload(team) {
  if (!team?.club_id) return null;
  const name = teamClubLabel(team);
  return {
    club_id: team.club_id,
    club_name: name,
    club_image: team.club_image || null,
  };
}

/**
 * Next upcoming fixture for a club team (by league_team_id).
 */
function nextFixtureForClubTeam(fixtures, leagueTeamId, today) {
  const tid = String(leagueTeamId || "");
  if (!tid) return null;
  const day = String(today || "").slice(0, 10);
  const mine = (fixtures || []).filter(
    (f) =>
      String(f.home?.league_team_id) === tid ||
      String(f.away?.league_team_id) === tid
  );
  const upcoming = mine
    .filter((f) => String(f.match_date || "") >= day)
    .sort((a, b) =>
      String(a.match_date || "").localeCompare(String(b.match_date || ""))
    );
  return upcoming[0] || null;
}

/** Normalize FLTT grade for same-level alternate matching (B2 ≈ b2). */
function normalizePresenceClassement(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "");
}

function presenceRankingPoints(player) {
  const n = Number(
    player?.ranking_points ?? player?.fltt_ranking_points ?? NaN
  );
  return Number.isFinite(n) ? n : null;
}

/**
 * Alternate may not outrank the absent/replaced player on points, and may
 * not come from (or have played) a superior team/division.
 */
function isEligiblePresenceAlternate(alt, {
  teamNumber,
  referencePlayers,
  highestTeamPlayedByUser = {},
  lockedFromSuperior = null,
}) {
  const cls = normalizePresenceClassement(
    alt?.classement || alt?.fltt_classement
  );
  if (!cls || !alt?.user_id) return false;

  if (lockedFromSuperior?.has(String(alt.user_id))) return false;

  const refs = (referencePlayers || []).filter(
    (r) =>
      normalizePresenceClassement(r.classement || r.fltt_classement) === cls
  );
  if (!refs.length) return false;

  const altPts = presenceRankingPoints(alt);
  const pointsOk = refs.some((ref) => {
    const refPts = presenceRankingPoints(ref);
    if (refPts == null) return true;
    if (altPts == null) return true;
    return altPts <= refPts;
  });
  if (!pointsOk) return false;

  const teamN = Math.max(1, Number(teamNumber) || 1);
  const rosterTeam = Number(alt.team_number);
  if (Number.isFinite(rosterTeam) && rosterTeam > 0 && rosterTeam < teamN) {
    return false;
  }
  const played = Number(
    highestTeamPlayedByUser[String(alt.user_id)] ??
      highestTeamPlayedByUser[alt.user_id]
  );
  if (Number.isFinite(played) && played > 0 && played < teamN) {
    return false;
  }
  return true;
}

function kindIcon(kind) {
  if (kind === "cup") return Flag;
  if (kind === "championship") return Trophy;
  return Medal;
}

function fixtureHasOfficialResult(fixture, matchByFixture) {
  const liveRow = matchByFixture.get(String(fixture?.fixture_id || ""));
  return Boolean(matchScoreFromRow(liveRow)?.complete);
}

/**
 * Crawl FLTT only when the latest match that should already have a score
 * does not. A complete last result, with the next match already on the
 * calendar, means this team is current.
 */
function needsAutoFlttResultsSync(fixtures, matchByFixture, today) {
  const day = String(today || "").slice(0, 10);
  const rows = (fixtures || []).filter((f) =>
    String(f?.match_date || "").slice(0, 10)
  );
  if (!rows.length) return false;
  const played = rows
    .filter((f) => String(f.match_date).slice(0, 10) < day)
    .sort((a, b) => String(b.match_date).localeCompare(String(a.match_date)));
  const todayMatch = rows.find(
    (f) => String(f.match_date).slice(0, 10) === day
  );
  const last =
    todayMatch && fixtureHasOfficialResult(todayMatch, matchByFixture)
      ? todayMatch
      : played[0];
  if (!last || fixtureHasOfficialResult(last, matchByFixture)) return false;
  return true;
}

function isDiv6Label(value) {
  return /^DIV\s*6\b/i.test(String(value || ""));
}

/** One fixture list per club DIV 6 team, even when another team is on screen. */
function div6ClubFixtureGroups(schedule) {
  const clubId = schedule?.club_id ? String(schedule.club_id) : "";
  const known = new Set(
    [...(schedule?.club_teams || []), ...(schedule?.my_teams || [])]
      .filter((team) => isDiv6Label(team?.division))
      .map((team) => team.league_team_id)
  );
  const groups = new Map();
  for (const fixture of schedule?.fixtures || []) {
    const homeId = fixture?.home?.league_team_id;
    const awayId = fixture?.away?.league_team_id;
    let teamId = null;
    if (known.has(homeId)) teamId = homeId;
    else if (known.has(awayId)) teamId = awayId;
    else if (
      isDiv6Label(fixture?.division) ||
      isDiv6Label(fixture?.home?.division) ||
      isDiv6Label(fixture?.away?.division)
    ) {
      if (clubId && String(fixture?.home?.club_id || "") === clubId) teamId = homeId;
      else if (clubId && String(fixture?.away?.club_id || "") === clubId) {
        teamId = awayId;
      }
    }
    if (!teamId) continue;
    if (!groups.has(teamId)) groups.set(teamId, []);
    groups.get(teamId).push(fixture);
  }
  return [...groups.values()];
}

function autoFlttSyncStorageKey(clubId, today) {
  return `smarttt-fltt-auto-sync:${clubId || "x"}:${today}`;
}

function autoFlttSyncAlreadyTried(clubId, today) {
  try {
    return sessionStorage.getItem(autoFlttSyncStorageKey(clubId, today)) === "1";
  } catch {
    return false;
  }
}

function markAutoFlttSyncTried(clubId, today) {
  try {
    sessionStorage.setItem(autoFlttSyncStorageKey(clubId, today), "1");
  } catch {
    /* private mode */
  }
}

export default function CompetitionPage() {
  const { t, i18n } = useTranslation();
  const { themeMode, brand } = useBrand();
  const locale = i18n.language || "en";
  const navigate = useNavigate();
  const phoneClips = useChampionshipClipUploads({ onlyIfSaved: true });
  const [user, setUser] = useState(() => readUser());
  const [clubId, setClubId] = useState(() => readCompetitionClubId());
  const [homeRole, setHomeRole] = useState(() => readActiveHomeRole(readUser()));
  const canStaffPresence =
    canManageChampionshipMatchPresence(user, { homeRole }) ||
    userHasAdminRole(user) ||
    userHasCommitteeRole(user);
  const isCoach = canStaffPresence;
  const isAdmin = homeRole === "admin" || userHasModeratorRole(user);
  const showTacticalPatternsLink = isPlayerAccount(user, { homeRole });

  useEffect(() => {
    const sync = () => {
      const next = readUser();
      setUser(next);
      setClubId(readCompetitionClubId());
      setHomeRole(readActiveHomeRole(next));
    };
    window.addEventListener("smarttt-home-role-changed", sync);
    window.addEventListener("smarttt-club-changed", sync);
    window.addEventListener("smarttt-user-updated", sync);
    return () => {
      window.removeEventListener("smarttt-home-role-changed", sync);
      window.removeEventListener("smarttt-club-changed", sync);
      window.removeEventListener("smarttt-user-updated", sync);
    };
  }, []);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [events, setEvents] = useState([]);
  const [hubTab, setHubTab] = useState("championship");
  const [clubCard, setClubCard] = useState(null);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [browseTeam, setBrowseTeam] = useState(null);
  const [browseFixtures, setBrowseFixtures] = useState([]);
  const [rsvps, setRsvps] = useState([]);
  /** team_number → user ids marked out on that team's next fixture */
  const [presenceOutsByTeam, setPresenceOutsByTeam] = useState({});
  const [oppPreviewOverride, setOppPreviewOverride] = useState(null);
  const [officialMatchSnap, setOfficialMatchSnap] = useState(null);
  const [opponentMemoryNotes, setOpponentMemoryNotes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [matchdayFilter, setMatchdayFilter] = useState(MATCHES_TAB);
  const [activeFixtureId, setActiveFixtureId] = useState("");
  const [liveMatches, setLiveMatches] = useState([]);
  const [syncingResults, setSyncingResults] = useState(false);
  const [syncNotice, setSyncNotice] = useState("");
  const [resultsLookup, setResultsLookup] = useState("");
  const autoSyncedFixtures = useRef(new Set());
  const feltenCupsTabTried = useRef(false);
  const [flttTournaments, setFlttTournaments] = useState([]);
  const [flttLoading, setFlttLoading] = useState(false);
  const [flttError, setFlttError] = useState("");
  const [expandedTournamentId, setExpandedTournamentId] = useState("");
  const [selectedCategories, setSelectedCategories] = useState({});
  const [feltenCup, setFeltenCup] = useState(null);
  const [feltenLoading, setFeltenLoading] = useState(false);
  const [feltenError, setFeltenError] = useState("");
  const [feltenSyncing, setFeltenSyncing] = useState(false);
  /** Same breakpoints as homepage group Slider (`slidesToShow`). */
  const [teamSlidesToShow, setTeamSlidesToShow] = useState(2);
  const [scheduleGen, setScheduleGen] = useState(0);
  const [directoryTeams, setDirectoryTeams] = useState(null);
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const tourDemo = useTourDemoActive();

  const loadFlttTournaments = useCallback(
    async ({ refresh = false, cached = false, silent = false } = {}) => {
      const lu = isLuxembourgFederationClub(federationClubFromUser(user, brand));
      if (!lu && !refresh) {
        setFlttTournaments([]);
        return [];
      }
      if (!silent) {
        setFlttLoading(true);
        setFlttError("");
      }
      try {
        const data = await fetchFlttTournaments({ refresh, cached });
        const list = Array.isArray(data?.tournaments) ? data.tournaments : [];
        setFlttTournaments(list);
        return list;
      } catch (err) {
        if (!silent) setFlttError(err?.response?.data?.message || err.message);
        return [];
      } finally {
        if (!silent) setFlttLoading(false);
      }
    },
    [user, brand]
  );

  const loadFeltenCup = useCallback(
    async ({ refresh = false, cached = false, silent = false } = {}) => {
      const lu = isLuxembourgFederationClub(federationClubFromUser(user, brand));
      if (!lu) {
        setFeltenCup(null);
        return null;
      }
      if (!silent) {
        setFeltenLoading(true);
        setFeltenError("");
      }
      try {
        const data = await fetchFeltenCup({ clubId, refresh, cached });
        setFeltenCup(data || { registered: false });
        return data;
      } catch (err) {
        if (!silent) setFeltenError(err?.response?.data?.message || err.message);
        return null;
      } finally {
        if (!silent) setFeltenLoading(false);
      }
    },
    [user, brand, clubId]
  );

  const onSyncFeltenCup = useCallback(async () => {
    if (feltenSyncing) return;
    setFeltenSyncing(true);
    setFeltenError("");
    try {
      const data = await syncFeltenCup({ clubId });
      setFeltenCup(data || { registered: false });
    } catch (err) {
      setFeltenError(err?.response?.data?.message || err.message);
    } finally {
      setFeltenSyncing(false);
    }
  }, [clubId, feltenSyncing]);

  const openFeltenMatchRoom = useCallback(
    async (fixture, { sheetPhoto = false, scorecam = false } = {}) => {
      if (busy) return;
      setBusy(true);
      setFeltenError("");
      try {
        const data = await openFeltenCupMatch({
          clubId,
          fixtureId: fixture?.fixture_id || null,
          fixtureKey: fixture?.fixture_key || null,
          useNext: !fixture?.fixture_id && !fixture?.fixture_key,
        });
        const id = data?.match?.championship_match_id;
        if (!id) return;
        const qs = new URLSearchParams(
          scorecam ? { tab: "camera", cam: "scorecam" } : { tab: "lineup" }
        );
        if (sheetPhoto) qs.set("sheetPhoto", "1");
        navigate(`/competition/match/${id}?${qs.toString()}`);
      } catch (err) {
        setFeltenError(err?.response?.data?.message || err.message);
      } finally {
        setBusy(false);
      }
    },
    [busy, clubId, navigate]
  );

  const onRecordFeltenMatch = useCallback(
    (fixture) => openFeltenMatchRoom(fixture, { sheetPhoto: false }),
    [openFeltenMatchRoom]
  );

  const onScorecamFeltenMatch = useCallback(
    (fixture) => openFeltenMatchRoom(fixture, { scorecam: true }),
    [openFeltenMatchRoom]
  );

  const onPhotographFeltenSheet = useCallback(
    (fixture) => openFeltenMatchRoom(fixture, { sheetPhoto: true }),
    [openFeltenMatchRoom]
  );

  const load = useCallback(async ({ background = false } = {}) => {
    if (!background) {
      setLoading(true);
      setError("");
    }
    try {
      const view = await fetchMyLeagueSchedule({
        lang: locale,
        clubId,
        lite: true,
      });
      setData(view);
      setScheduleGen((n) => n + 1);
      setBrowseTeam(null);
      setSelectedTeamId((prev) => {
        const known = [
          ...(view?.my_teams || []),
          ...(view?.club_teams || []),
          ...(view?.all_teams || []),
        ];
        if (prev && known.some((team) => team.league_team_id === prev)) {
          return prev;
        }
        const preferred =
          (view?.my_teams || []).find((team) => Number(team.team_number) === 1) ||
          view?.my_teams?.[0] ||
          (view?.club_teams || []).find((team) => Number(team.team_number) === 1) ||
          view?.club_teams?.[0] ||
          null;
        return preferred?.league_team_id || null;
      });
      void listChampionshipMatches({ clubId })
        .then((matches) => {
          if (Array.isArray(matches)) setLiveMatches(matches);
        })
        .catch(() => {});
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, [locale, clubId]);

  const schedule = useMemo(() => {
    if (data?.season) return data;
    if (tourDemo) return demoCompetitionView();
    return data;
  }, [data, tourDemo]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!scheduleGen || tourDemo) return undefined;
    let cancelled = false;
    fetchMyLeagueRoster({ lang: locale, clubId })
      .then((bundle) => {
        if (cancelled || !bundle) return;
        setData((prev) => (prev ? { ...prev, ...bundle } : prev));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [scheduleGen, locale, clubId, tourDemo]);

  useEffect(() => {
    if (!clubId || tourDemo) return undefined;
    let cancelled = false;
    const run = () => {
      listCompetitions({ clubId })
        .then((comps) => {
          if (!cancelled) setEvents(Array.isArray(comps) ? comps : []);
        })
        .catch(() => {});
    };
    const useIdle = typeof window.requestIdleCallback === "function";
    const idleId = useIdle
      ? window.requestIdleCallback(run, { timeout: 1600 })
      : window.setTimeout(run, 500);
    return () => {
      cancelled = true;
      if (useIdle) window.cancelIdleCallback(idleId);
      else window.clearTimeout(idleId);
    };
  }, [clubId, tourDemo]);

  useEffect(() => {
    setDirectoryTeams(null);
  }, [clubId]);

  useEffect(() => {
    if (!pickerOpen || directoryTeams) return undefined;
    let cancelled = false;
    setDirectoryLoading(true);
    fetchLeagueSeason({ clubId })
      .then((view) => {
        if (!cancelled) setDirectoryTeams(view?.teams || []);
      })
      .catch(() => {
        if (!cancelled) setDirectoryTeams([]);
      })
      .finally(() => {
        if (!cancelled) setDirectoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [pickerOpen, directoryTeams, clubId]);

  /** Cups and tournaments stay off the first paint. Open the tab to load them. */
  useEffect(() => {
    if (hubTab !== "cups") {
      feltenCupsTabTried.current = false;
      return undefined;
    }
    if (!isLuxembourgFederationClub(federationClubFromUser(user, brand))) {
      return undefined;
    }
    if (feltenCupsTabTried.current) return undefined;
    feltenCupsTabTried.current = true;
    let cancelled = false;
    (async () => {
      await loadFeltenCup({ cached: true });
      if (cancelled) return;
      await loadFeltenCup({ silent: true });
    })();
    return () => {
      cancelled = true;
    };
  }, [hubTab, user, brand, loadFeltenCup]);

  useEffect(() => {
    if (hubTab !== "tournaments") return undefined;
    if (!isLuxembourgFederationClub(federationClubFromUser(user, brand))) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      await loadFlttTournaments({ cached: true });
      if (cancelled) return;
      await loadFlttTournaments({ silent: true });
    })();
    return () => {
      cancelled = true;
    };
  }, [hubTab, user, brand, loadFlttTournaments]);

  useEffect(() => {
    if (!pickerOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setPickerOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pickerOpen]);

  const selectedTeam = useMemo(() => {
    if (browseTeam) return browseTeam;
    const own = [...(schedule?.my_teams || []), ...(schedule?.club_teams || [])];
    return own.find((team) => team.league_team_id === selectedTeamId) || own[0] || null;
  }, [browseTeam, schedule, selectedTeamId]);

  const standingsTables = useMemo(
    () => standingsTablesFromSchedule(schedule),
    [schedule]
  );
  const selectedStandings = useMemo(
    () => standingsTableForTeam(standingsTables, selectedTeam),
    [standingsTables, selectedTeam]
  );
  const selectedStandingsRow = useMemo(
    () => standingsRowForTeam(selectedStandings, selectedTeam),
    [selectedStandings, selectedTeam]
  );

  useEffect(() => {
    // Keep the club row visible inside the ladder board only — never scroll the
    // page (bottom-nav → Competition must open at the top, not on classement).
    const row = document.querySelector(".comp-ladder__row.is-us");
    const board = row?.closest(".comp-ladder__board");
    if (!row || !board) return;
    const boardRect = board.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    if (rowRect.top < boardRect.top) {
      board.scrollTop -= boardRect.top - rowRect.top;
    } else if (rowRect.bottom > boardRect.bottom) {
      board.scrollTop += rowRect.bottom - boardRect.bottom;
    }
  }, [selectedStandings, selectedTeamId]);

  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      if (w < 900) setTeamSlidesToShow(2);
      else if (w < 1150) setTeamSlidesToShow(3);
      else setTeamSlidesToShow(4);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const fixtures = useMemo(() => {
    const all = browseTeam ? browseFixtures : schedule?.fixtures || [];
    if (!selectedTeam) return browseTeam ? all : [];
    return all.filter(
      (f) =>
        f.home?.league_team_id === selectedTeam.league_team_id ||
        f.away?.league_team_id === selectedTeam.league_team_id
    );
  }, [browseTeam, browseFixtures, schedule, selectedTeam]);

  const filteredFixtures = useMemo(() => {
    if (!matchdayFilter || matchdayFilter === MATCHES_TAB) return fixtures;
    return fixtures.filter((f) => f.matchday?.code === matchdayFilter);
  }, [fixtures, matchdayFilter]);
  const isMatchesTab = matchdayFilter === MATCHES_TAB;

  const today = localIsoDate();
  const upcomingChampionshipFixture =
    filteredFixtures.find((f) => String(f.match_date || "") >= today) || null;
  const lastPlayedFixture =
    [...filteredFixtures]
      .reverse()
      .find((f) => {
        const d = String(f.match_date || "");
        return d && d < today;
      }) || null;
  /** Prefer the next championship fixture; fall back to the latest result. */
  const nextFixture =
    upcomingChampionshipFixture || lastPlayedFixture || filteredFixtures[0] || null;
  const highlightedFixture =
    filteredFixtures.find((f) => f.fixture_id === activeFixtureId) || nextFixture;

  const groupedFixtures = useMemo(() => {
    const map = new Map();
    for (const f of filteredFixtures) {
      const code = f.matchday?.code || "—";
      if (!map.has(code)) map.set(code, []);
      map.get(code).push(f);
    }
    return [...map.entries()];
  }, [filteredFixtures]);

  useEffect(() => {
    if (!highlightedFixture?.fixture_id) {
      setRsvps([]);
      return undefined;
    }
    let cancelled = false;
    fetchLeagueFixtureRsvps(highlightedFixture.fixture_id)
      .then((rows) => {
        if (!cancelled) setRsvps(Array.isArray(rows) ? rows : []);
      })
      .catch(() => {
        if (!cancelled) setRsvps([]);
      });
    return () => {
      cancelled = true;
    };
  }, [highlightedFixture?.fixture_id]);

  /** Load out-lists for every club team's next fixture (cascade locks). */
  useEffect(() => {
    if (browseTeam || !schedule?.fixtures?.length) {
      setPresenceOutsByTeam({});
      return undefined;
    }
    const selectedN = Math.max(1, Number(selectedTeam?.team_number) || 1);
    const teams = [...(schedule.club_teams || [])]
      .filter((team) => Math.max(1, Number(team.team_number) || 1) <= selectedN)
      .sort((a, b) => Number(a.team_number) - Number(b.team_number));
    if (!teams.length) {
      setPresenceOutsByTeam({});
      return undefined;
    }
    const day = localIsoDate();
    let cancelled = false;
    (async () => {
      const nextMap = {};
      await Promise.all(
        teams.map(async (team) => {
          const fx = nextFixtureForClubTeam(
            schedule.fixtures,
            team.league_team_id,
            day
          );
          const tn = Math.max(1, Number(team.team_number) || 1);
          if (!fx?.fixture_id) {
            nextMap[tn] = [];
            return;
          }
          try {
            const rows = await fetchLeagueFixtureRsvps(fx.fixture_id);
            nextMap[tn] = presenceOutsFromRsvps(rows);
          } catch {
            nextMap[tn] = [];
          }
        })
      );
      if (!cancelled) setPresenceOutsByTeam(nextMap);
    })();
    return () => {
      cancelled = true;
    };
  }, [browseTeam, schedule?.fixtures, schedule?.club_teams, selectedTeam?.team_number, selectedTeam?.league_team_id]);

  /** Keep the selected team's outs in sync with live RSVP edits. */
  useEffect(() => {
    const tn = Math.max(1, Number(selectedTeam?.team_number) || 1);
    if (!selectedTeam || browseTeam) return;
    const next = presenceOutsFromRsvps(rsvps);
    setPresenceOutsByTeam((prev) => {
      const prevRow = Array.isArray(prev[tn]) ? prev[tn] : [];
      if (
        prevRow.length === next.length &&
        prevRow.every((id, i) => String(id) === String(next[i]))
      ) {
        return prev;
      }
      return { ...prev, [tn]: next };
    });
  }, [rsvps, selectedTeam, browseTeam]);

  useEffect(() => {
    setOppPreviewOverride(null);
  }, [highlightedFixture?.fixture_id]);

  const myRsvp = useMemo(() => {
    const uid = user?.user_id;
    if (!uid) return highlightedFixture?.rsvp?.status || "pending";
    const row = rsvps.find((r) => String(r.user_id) === String(uid));
    return row?.status || highlightedFixture?.rsvp?.status || "pending";
  }, [rsvps, user, highlightedFixture]);

  /** Full club ranking pool (teams 1..N + reserve), excluding phase absent/benched/unknown. */
  const rankingPool = useMemo(
    () =>
      rankedPool({
        teams: schedule?.roster_by_team,
        reserve: schedule?.roster_reserve,
        absent: schedule?.roster_absent,
        benched: schedule?.roster_benched,
        unknown: schedule?.roster_unknown,
      }),
    [schedule]
  );

  /**
   * Effective four for this fixture: cascade from team 1 so players already
   * selected on a superior team stay locked; players replaced (out) on a
   * superior team remain available for this lower division.
   */
  const matchPresenceCascade = useMemo(() => {
    const n = Math.max(1, Number(selectedTeam?.team_number) || 1);
    const outsByTeam = { ...presenceOutsByTeam };
    outsByTeam[n] = presenceOutsFromRsvps(rsvps);
    return cascadeMatchSquad(rankingPool, n, outsByTeam);
  }, [rankingPool, selectedTeam, rsvps, presenceOutsByTeam]);

  const matchSquadPlayers = matchPresenceCascade.squad;
  const lockedFromSuperiorTeams = matchPresenceCascade.lockedFromSuperior;

  /** RSVP-outs who would re-enter this team's four if restored. */
  const matchAbsentPlayers = useMemo(() => {
    const n = Math.max(1, Number(selectedTeam?.team_number) || 1);
    const outSet = new Set(presenceOutsFromRsvps(rsvps));
    const remaining = rankingPool.filter(
      (p) => !lockedFromSuperiorTeams.has(String(p.user_id))
    );
    return remaining.filter((p) => {
      const id = String(p.user_id);
      if (!outSet.has(id)) return false;
      const available = remaining.filter(
        (q) => String(q.user_id) === id || !outSet.has(String(q.user_id))
      );
      return available.slice(0, 4).some((q) => String(q.user_id) === id);
    });
  }, [rankingPool, selectedTeam, rsvps, lockedFromSuperiorTeams]);

  /**
   * Same-classement players outside the effective four who could take a seat
   * on this team (coach/admin picks who plays). Must not outrank the
   * absent/replaced player on points, nor have played a superior division,
   * nor already be selected on a superior team.
   */
  const matchAlternatePlayers = useMemo(() => {
    const teamN = Math.max(1, Number(selectedTeam?.team_number) || 1);
    const squadIds = new Set(
      matchSquadPlayers.map((p) => String(p.user_id)).filter(Boolean)
    );
    const rsvpOut = new Set(presenceOutsFromRsvps(rsvps));
    const highestTeamPlayedByUser =
      schedule?.roster_highest_team_played || {};
    const referencePlayers = matchAbsentPlayers.length
      ? matchAbsentPlayers
      : matchSquadPlayers;
    if (!referencePlayers.length) return [];

    return rankingPool.filter((p) => {
      const id = String(p.user_id);
      if (!id || squadIds.has(id) || rsvpOut.has(id)) return false;
      if (lockedFromSuperiorTeams.has(id)) return false;
      return isEligiblePresenceAlternate(p, {
        teamNumber: teamN,
        referencePlayers,
        highestTeamPlayedByUser,
        lockedFromSuperior: lockedFromSuperiorTeams,
      });
    });
  }, [
    rankingPool,
    matchSquadPlayers,
    matchAbsentPlayers,
    rsvps,
    selectedTeam,
    schedule,
    lockedFromSuperiorTeams,
  ]);

  const playableTeamIds = useMemo(
    () => new Set((schedule?.my_teams || []).map((team) => team.league_team_id)),
    [schedule]
  );
  const matchByFixture = useMemo(() => {
    const map = new Map();
    for (const row of liveMatches) {
      if (row?.fixture_id) map.set(String(row.fixture_id), row);
    }
    return map;
  }, [liveMatches]);

  const highlightedScore = scoreForFixture(
    highlightedFixture,
    highlightedFixture?.fixture_id
      ? matchByFixture.get(String(highlightedFixture.fixture_id))
      : null
  );
  const highlightedOutcome = clubMatchOutcome(
    highlightedScore,
    highlightedFixture,
    selectedTeam
  );
  const highlightedIsPast = fixtureIsPast(highlightedFixture?.match_date, today);
  const presenceLocked = matchPresenceLocked({
    matchDate: highlightedFixture?.match_date,
    today,
    scoreComplete: highlightedScore?.complete,
  });
  const waitingOfficialResults = Boolean(
    highlightedIsPast &&
      !highlightedScore?.complete &&
      !(
        highlightedScore?.live &&
        Number(highlightedScore?.home) + Number(highlightedScore?.away) > 0
      ) &&
      !browseTeam
  );
  const showOfficialLineups = Boolean(
    !browseTeam &&
      (highlightedIsPast || highlightedScore?.complete) &&
      highlightedFixture?.fixture_id
  );
  const highlightedMatchId = highlightedFixture?.fixture_id
    ? matchByFixture.get(String(highlightedFixture.fixture_id))
        ?.championship_match_id || null
    : null;

  useEffect(() => {
    const fixtureId = highlightedFixture?.fixture_id;
    if (
      !fixtureId ||
      browseTeam ||
      highlightedIsPast ||
      highlightedFixture?.opponent_preview
    ) {
      return undefined;
    }
    let cancelled = false;
    fetchLeagueFixtureOpponentPreview(fixtureId)
      .then((preview) => {
        if (cancelled || !preview) return;
        setData((prev) => {
          if (!prev?.fixtures) return prev;
          let changed = false;
          const fixtures = prev.fixtures.map((row) => {
            if (String(row.fixture_id) !== String(fixtureId)) return row;
            if (row.opponent_preview) return row;
            changed = true;
            return { ...row, opponent_preview: preview };
          });
          return changed ? { ...prev, fixtures } : prev;
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [
    highlightedFixture?.fixture_id,
    highlightedFixture?.opponent_preview,
    highlightedIsPast,
    browseTeam,
    scheduleGen,
  ]);

  useEffect(() => {
    setOfficialMatchSnap(null);
    if (browseTeam || !highlightedMatchId) return undefined;
    let cancelled = false;
    fetchChampionshipMatch(highlightedMatchId)
      .then((snap) => {
        if (!cancelled && snap) setOfficialMatchSnap(snap);
      })
      .catch(() => {
        if (!cancelled) setOfficialMatchSnap(null);
      });
    return () => {
      cancelled = true;
    };
  }, [browseTeam, highlightedMatchId]);

  const openLiveMatch = useCallback(
    async (fixtureId, { tab } = {}) => {
      if (!fixtureId || busy) return;
      setBusy(true);
      const tabSuffix = tab === "camera" ? "?tab=camera" : "";
      try {
        const existing = matchByFixture.get(String(fixtureId));
        if (existing?.championship_match_id && existing.status !== "lineup") {
          navigate(
            `/competition/match/${existing.championship_match_id}${tabSuffix}`
          );
          return;
        }
        const opened = await openChampionshipMatch(fixtureId);
        const id = opened?.match?.championship_match_id;
        if (id) navigate(`/competition/match/${id}${tabSuffix}`);
      } catch (err) {
        setError(err?.response?.data?.message || err.message || String(err));
      } finally {
        setBusy(false);
      }
    },
    [busy, matchByFixture, navigate]
  );

  const openClubCard = useCallback((team) => {
    const payload = clubCardPayload(team);
    if (payload) setClubCard(payload);
  }, []);

  const onMatchCardTap = useCallback(
    (event, fixtureId) => {
      if (!fixtureId || browseTeam || busy) return;
      if (
        event.target.closest(
          "button, a, input, select, textarea, .comp-rsvp, .comp-presence-block, .comp-opp-preview"
        )
      ) {
        return;
      }
      void openLiveMatch(fixtureId);
    },
    [browseTeam, busy, openLiveMatch]
  );
  const canRsvp =
    !isCoach &&
    Boolean(selectedTeam) &&
    playableTeamIds.has(selectedTeam.league_team_id) &&
    !presenceLocked;

  const onRsvp = async (status) => {
    if (!canRsvp || !highlightedFixture?.fixture_id || busy) return;
    setBusy(true);
    try {
      const result = await setLeagueFixtureRsvp(highlightedFixture.fixture_id, status);
      setRsvps(result?.rsvps || []);
      await load({ background: true });
    } catch (err) {
      window.alert(err?.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const onPickTeam = async (team, { foreign = false } = {}) => {
    setPickerOpen(false);
    setQuery("");
    setMatchdayFilter(MATCHES_TAB);
    setActiveFixtureId("");
    setHubTab("championship");
    if (!foreign) {
      setBrowseTeam(null);
      setBrowseFixtures([]);
      setSelectedTeamId(team.league_team_id);
      return;
    }
    setBrowseTeam(team);
    try {
      const view = await fetchLeagueFixtures({
        team: team.display_name || `${team.club_short_name} ${team.team_number}`,
      });
      setBrowseFixtures(view?.fixtures || []);
    } catch {
      setBrowseFixtures([]);
    }
  };

  const allTeams = directoryTeams || schedule?.all_teams || [];
  const clubTeams = useMemo(() => {
    const fromApi = schedule?.club_teams || [];
    if (fromApi.length) {
      return [...fromApi].sort((a, b) => Number(a.team_number) - Number(b.team_number));
    }
    const clubId = schedule?.club_id;
    const mine = schedule?.my_teams || [];
    const shorts = new Set(mine.map((team) => fold(team.club_short_name)).filter(Boolean));
    const clubs = allTeams.filter((team) => {
      if (clubId && team.club_id && String(team.club_id) === String(clubId)) return true;
      return shorts.has(fold(team.club_short_name));
    });
    const list = clubs.length ? clubs : mine;
    return [...list].sort((a, b) => Number(a.team_number) - Number(b.team_number));
  }, [schedule, allTeams]);
  const teamSlidesVisible = Math.min(teamSlidesToShow, Math.max(clubTeams.length, 1));
  const canSlideTeams = clubTeams.length > teamSlidesVisible;
  const teamSlideDefaultIndex = useMemo(() => {
    if (browseTeam) return 0;
    const idx = clubTeams.findIndex(
      (team) => team.league_team_id === selectedTeam?.league_team_id
    );
    return idx >= 0 ? idx : 0;
  }, [browseTeam, clubTeams, selectedTeam?.league_team_id]);
  const q = fold(query);
  const teamMatchesQuery = (team) => {
    if (!q) return true;
    return (
      fold(team.display_name).includes(q) ||
      fold(team.club_short_name).includes(q) ||
      fold(team.division).includes(q)
    );
  };
  const visibleClubTeams = clubTeams.filter(teamMatchesQuery);
  const otherTeams = allTeams
    .filter((team) => !clubTeams.some((c) => c.league_team_id === team.league_team_id))
    .filter(teamMatchesQuery)
    .sort(
      (a, b) =>
        fold(a.club_short_name).localeCompare(fold(b.club_short_name)) ||
        Number(a.team_number) - Number(b.team_number)
    );

  const canManagePresence =
    canStaffPresence &&
    !browseTeam &&
    Boolean(selectedTeam) &&
    clubTeams.some((team) => team.league_team_id === selectedTeam.league_team_id);

  const onSetPlayerRsvp = async (playerUserId, status) => {
    if (!canManagePresence || presenceLocked || !highlightedFixture?.fixture_id || busy) {
      return;
    }
    setBusy(true);
    try {
      const result = await setLeagueFixtureRsvp(highlightedFixture.fixture_id, status, {
        playerUserId,
      });
      setRsvps(result?.rsvps || []);
    } catch (err) {
      window.alert(err?.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Put a same-classement alternate into the team's four by benching the
   * marginal same-level squad player (and any same-level blockers).
   */
  const onSelectAlternate = async (alt) => {
    if (
      !canManagePresence ||
      presenceLocked ||
      !highlightedFixture?.fixture_id ||
      busy ||
      !alt?.user_id
    ) {
      return;
    }
    const cls = normalizePresenceClassement(
      alt.classement || alt.fltt_classement
    );
    if (!cls) return;
    const n = Math.max(1, Number(selectedTeam?.team_number) || 1);
    const start = (n - 1) * 4;
    const sameInSquad = matchSquadPlayers.filter(
      (p) =>
        normalizePresenceClassement(p.classement || p.fltt_classement) === cls
    );
    const sameAbsent = matchAbsentPlayers.filter(
      (p) =>
        normalizePresenceClassement(p.classement || p.fltt_classement) === cls
    );
    const referencePlayers = sameAbsent.length ? sameAbsent : sameInSquad;
    if (
      !isEligiblePresenceAlternate(alt, {
        teamNumber: n,
        referencePlayers,
        highestTeamPlayedByUser: schedule?.roster_highest_team_played || {},
        lockedFromSuperior: lockedFromSuperiorTeams,
      })
    ) {
      window.alert(
        t(
          "competitionAlternateNotEligible",
          "This player is not eligible: ranking points must not exceed the absent player, they must not have played a superior division, and they must not already be selected on a higher team."
        )
      );
      return;
    }

    // Prefer displacing an absent seat's replacement path: bench the
    // same-class squad member with the highest points the alt can still match.
    const altPts = presenceRankingPoints(alt);
    const displaceCandidates = [...sameInSquad].sort((a, b) => {
      const pa = presenceRankingPoints(a);
      const pb = presenceRankingPoints(b);
      if (pa == null && pb == null) return 0;
      if (pa == null) return 1;
      if (pb == null) return -1;
      return pb - pa;
    });
    const displace =
      displaceCandidates.find((ref) => {
        const refPts = presenceRankingPoints(ref);
        if (refPts == null || altPts == null) return true;
        return altPts <= refPts;
      }) ||
      displaceCandidates[displaceCandidates.length - 1] ||
      matchSquadPlayers[matchSquadPlayers.length - 1];
    if (!displace?.user_id || String(displace.user_id) === String(alt.user_id)) {
      return;
    }

    const outSet = new Set(
      rsvps.filter((r) => r.status === "out").map((r) => String(r.user_id))
    );
    outSet.add(String(displace.user_id));
    outSet.delete(String(alt.user_id));

    for (let i = 0; i < 8; i += 1) {
      const pool = rankingPool.filter((p) => {
        const id = String(p.user_id);
        if (id === String(alt.user_id)) return true;
        return !outSet.has(id);
      });
      const four = pool.slice(start, start + 4);
      if (four.some((p) => String(p.user_id) === String(alt.user_id))) break;
      const blocker = [...four]
        .reverse()
        .find(
          (p) =>
            String(p.user_id) !== String(alt.user_id) &&
            normalizePresenceClassement(p.classement || p.fltt_classement) ===
              cls
        );
      if (!blocker) break;
      outSet.add(String(blocker.user_id));
    }

    const prevOut = new Set(
      rsvps.filter((r) => r.status === "out").map((r) => String(r.user_id))
    );
    const toOut = [...outSet].filter((id) => !prevOut.has(id));
    const fixtureId = highlightedFixture.fixture_id;

    setBusy(true);
    try {
      let latest = rsvps;
      for (const id of toOut) {
        const result = await setLeagueFixtureRsvp(fixtureId, "out", {
          playerUserId: id,
        });
        latest = result?.rsvps || latest;
      }
      const result = await setLeagueFixtureRsvp(fixtureId, "in", {
        playerUserId: alt.user_id,
      });
      setRsvps(result?.rsvps || latest);
    } catch (err) {
      window.alert(err?.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const onSetOpponentAvailability = async (player, status) => {
    if (!canSetOpponentAvailability || !highlightedFixture?.fixture_id || busy) {
      return;
    }
    setBusy(true);
    try {
      const existing = matchByFixture.get(String(highlightedFixture.fixture_id));
      let snap = null;
      let matchId = existing?.championship_match_id;
      if (!matchId) {
        snap = await openChampionshipMatch(highlightedFixture.fixture_id);
        matchId = snap?.match?.championship_match_id;
      }
      if (!matchId) throw new Error("Match not found");
      snap = await setChampionshipOpponentAvailability(matchId, {
        status,
        side: opponentPreview?.side,
        player,
      });
      setOppPreviewOverride(opponentPreviewFromSnapshot(snap));
      const matches = await listChampionshipMatches().catch(() => null);
      if (Array.isArray(matches)) setLiveMatches(matches);
    } catch (err) {
      window.alert(err?.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const onGetFlttResults = useCallback(
    async ({ matchDate, silent = false, auto = false } = {}) => {
      if (syncingResults || tourDemo) return;
      setSyncingResults(true);
      if (!silent) {
        setSyncNotice("");
        setError("");
      }
      try {
        const data = await syncFlttMatchdayResults({
          matchDate,
          auto,
        });
        if (data?.auto_skipped) {
          return data;
        }
        const applied = Array.isArray(data?.applied) ? data.applied : [];
        const date = data?.match_date || matchDate || "";
        if (data?.unpublished || !applied.length) {
          if (!silent) {
            const later = t(
              "competitionResultsLater",
              "Results are not published yet. Try again later."
            );
            setSyncNotice(later);
          }
          setResultsLookup("later");
          await load({ background: true });
          return data;
        }
        setResultsLookup("");
        if (!silent) {
          setSyncNotice(
            t(
              "competitionResultsSynced",
              "Updated {{n}} club match{{plural}} from FLTT{{date}}.",
              {
                n: applied.length,
                plural: applied.length === 1 ? "" : "es",
                date: date ? ` (${date})` : "",
              }
            )
          );
        }
        await load({ background: true });
        return data;
      } catch (err) {
        const later = t(
          "competitionResultsLater",
          "Results are not published yet. Try again later."
        );
        setResultsLookup("later");
        if (!silent) {
          setError(
            err?.response?.data?.message ||
              err.message ||
              t("competitionResultsFailed", "Could not load FLTT results")
          );
          setSyncNotice(later);
        }
        return null;
      } finally {
        setSyncingResults(false);
      }
    },
    [syncingResults, tourDemo, t, load]
  );

  const toggleCategorySelection = (tournamentId, categoryId) => {
    setSelectedCategories((prev) => {
      const cur = new Set(prev[tournamentId] || []);
      if (cur.has(categoryId)) cur.delete(categoryId);
      else cur.add(categoryId);
      return { ...prev, [tournamentId]: [...cur] };
    });
  };

  const onRecordTournamentMatch = async (
    tournament,
    categoryId,
    { scorecam = false } = {}
  ) => {
    if (busy || !tournament?.tournament_id) return;
    setBusy(true);
    setError("");
    try {
      const data = await openPlayerTournamentMatch(tournament.tournament_id, categoryId);
      const id = data?.match?.championship_match_id;
      if (id) {
        navigate(
          `/competition/match/${id}${scorecam ? "?tab=camera&cam=scorecam" : ""}`
        );
      }
    } catch (err) {
      setError(err?.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const onNotifyTournamentParticipation = async (tournament, status = "in") => {
    if (busy || !tournament?.tournament_id) return;
    const cats = (tournament.categories || []).filter((c) => c.eligible !== false);
    let categoryIds = (selectedCategories[tournament.tournament_id] || []).filter((id) =>
      cats.some((c) => c.category_id === id)
    );
    if (cats.length && !categoryIds.length) {
      const alreadyIn = (tournament.my_rsvps || [])
        .filter((r) => (r.status === "in" || r.status === "pending") && r.category_id)
        .map((r) => r.category_id)
        .filter((id) => cats.some((c) => c.category_id === id));
      if (status === "out" && alreadyIn.length) {
        categoryIds = alreadyIn;
      } else if (status === "out") {
        categoryIds = (tournament.my_rsvps || [])
          .filter((r) => (r.status === "in" || r.status === "pending") && r.category_id)
          .map((r) => r.category_id);
      } else {
        window.alert(
          t(
            "competitionSelectCategory",
            "Select at least one category for this tournament."
          )
        );
        return;
      }
    }
    setBusy(true);
    try {
      const result = await setFlttTournamentRsvp(tournament.tournament_id, status, {
        categoryIds: cats.length || categoryIds.length ? categoryIds : undefined,
      });
      if (result?.tournament) {
        setFlttTournaments((prev) =>
          prev.map((t) =>
            t.tournament_id === result.tournament.tournament_id ? result.tournament : t
          )
        );
      } else {
        await loadFlttTournaments();
      }
    } catch (err) {
      window.alert(err?.response?.data?.message || err.message);
    } finally {
      setBusy(false);
    }
  };

  const matchdaysForTeam = useMemo(() => {
    const codes = [];
    const seen = new Set();
    for (const f of fixtures) {
      const code = f.matchday?.code;
      if (!code || seen.has(code)) continue;
      seen.add(code);
      codes.push(f.matchday);
    }
    return codes;
  }, [fixtures]);

  const otherEvents = useMemo(
    () => events.filter((e) => e.kind === "tournament" || e.kind === "cup"),
    [events]
  );
  const championshipEvent = useMemo(
    () => events.find((e) => e.kind === "championship") || null,
    [events]
  );

  const scheduledFlttTournaments = useMemo(() => {
    const day = localIsoDate();
    return (flttTournaments || [])
      .filter((event) => {
        const d = String(event.event_date || "").slice(0, 10);
        return !d || d >= day;
      })
      .sort((a, b) =>
        String(a.event_date || "").localeCompare(String(b.event_date || ""))
      );
  }, [flttTournaments]);

  const mode = themeMode === "light" ? "light" : "dark";
  const showSchedule = Boolean(schedule?.season);
  const waiting = loading && !showSchedule;
  const empty = !waiting && !showSchedule;
  const federationClub = federationClubFromUser(user, brand);
  const showFlttEmptyCopy = isLuxembourgFederationClub(federationClub);
  const showFranceEmptyCopy = isFranceFederationClub(federationClub);
  const showBelgiumCopy = isBelgiumClub(federationClub);
  const showGermanyCopy = isGermanyClub(federationClub);
  const seasonCategoryLabel =
    showBelgiumCopy ||
    showGermanyCopy ||
    String(schedule?.season?.category || "").toLowerCase() === "interclubs"
      ? t("competitionInterclubs", "Interclubs")
      : t("competitionSeniors", "Seniors");
  const canGetFlttResults =
    isCoach && showFlttEmptyCopy && Boolean(schedule?.season) && !tourDemo;
  const relative = relativeMatchLabel(highlightedFixture?.match_date, t);
  const nextIsHighlighted =
    highlightedFixture?.fixture_id &&
    highlightedFixture.fixture_id === nextFixture?.fixture_id;
  const nextMatchLive =
    Boolean(highlightedFixture?.fixture_id) &&
    matchByFixture.get(String(highlightedFixture.fixture_id))?.status === "live";
  const showNextChampionshipTeaser = Boolean(
    upcomingChampionshipFixture &&
      highlightedFixture &&
      String(upcomingChampionshipFixture.fixture_id) !==
        String(highlightedFixture.fixture_id) &&
      (highlightedIsPast ||
        highlightedScore?.complete ||
        waitingOfficialResults)
  );
  const officialOppPreview = useMemo(
    () =>
      officialMatchSnap ? opponentPreviewFromSnapshot(officialMatchSnap) : null,
    [officialMatchSnap]
  );
  const ourOfficialSide = selectedTeam
    ? isHomeForTeam(highlightedFixture, selectedTeam)
      ? "home"
      : "away"
    : null;
  const ourOfficialLineup =
    officialMatchSnap && ourOfficialSide
      ? officialLineupFromSnapshot(officialMatchSnap, ourOfficialSide)
      : [];
  const showSavedLineup = Boolean(
    !browseTeam && snapshotLineupSaved(officialMatchSnap)
  );
  const showStoredLineup = showOfficialLineups || showSavedLineup;
  const opponentPreview = useMemo(() => {
    if (oppPreviewOverride) return oppPreviewOverride;
    if (showStoredLineup && officialOppPreview) return officialOppPreview;
    if (nextIsHighlighted && !browseTeam && !highlightedIsPast) {
      return highlightedFixture?.opponent_preview || null;
    }
    return null;
  }, [
    oppPreviewOverride,
    showStoredLineup,
    officialOppPreview,
    nextIsHighlighted,
    browseTeam,
    highlightedIsPast,
    highlightedFixture?.opponent_preview,
  ]);
  const showOpponentPreview = Boolean(
    opponentPreview &&
      ((opponentPreview.titular || []).length ||
        (opponentPreview.extras || []).length) &&
      // Upcoming: theoretical preview until someone saves the real sheet.
      // Saved or complete: show the lineup stored on the match.
      (showStoredLineup ||
        (!highlightedScore?.complete && !highlightedIsPast))
  );
  const canSetOpponentAvailability = Boolean(
    showOpponentPreview &&
      !showStoredLineup &&
      !browseTeam &&
      String(highlightedFixture?.match_date || "") === localIsoDate() &&
      (canManagePresence || myRsvp === "in")
  );

  useEffect(() => {
    setOpponentMemoryNotes([]);
    if (
      !showOpponentPreview ||
      showOfficialLineups ||
      browseTeam ||
      tourDemo ||
      !schedule?.club_id
    ) {
      return undefined;
    }
    const pool = [
      ...(opponentPreview?.titular || []),
      ...(opponentPreview?.extras || []),
    ].slice(0, 8);
    if (!pool.length) return undefined;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      if (cancelled) return;
      Promise.all(
        pool.map((p) =>
          listChampionshipNotesAboutPlayer({
            club_id: schedule.club_id,
            user_id: p.user_id || undefined,
            player_id: p.player_id || undefined,
            display_name: p.display_name || p.name || undefined,
            limit: 6,
          }).catch(() => [])
        )
      ).then((lists) => {
      if (cancelled) return;
      const byId = new Map();
      for (const list of lists) {
        for (const n of list || []) {
          if (n?.note_id) byId.set(n.note_id, n);
        }
      }
      setOpponentMemoryNotes([...byId.values()]);
      });
    }, 800);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    showOpponentPreview,
    showOfficialLineups,
    browseTeam,
    tourDemo,
    schedule?.club_id,
    opponentPreview,
  ]);

  const nextRecLabel = nextMatchLive
    ? t("cmJoinLive", "Join live match")
    : highlightedScore?.complete
      ? t("competitionOpenResult", "Open result sheet")
      : t("cmRecordMatch", "Record match");

  const needsOfficialSync = useMemo(() => {
    if (!showFlttEmptyCopy || browseTeam) return false;
    const selectedNeeds =
      selectedTeam &&
      needsAutoFlttResultsSync(fixtures, matchByFixture, today);
    const div6Needs = div6ClubFixtureGroups(schedule).some((rows) =>
      needsAutoFlttResultsSync(rows, matchByFixture, today)
    );
    return Boolean(selectedNeeds || div6Needs);
  }, [
    showFlttEmptyCopy,
    browseTeam,
    selectedTeam,
    fixtures,
    schedule,
    matchByFixture,
    today,
  ]);

  useEffect(() => {
    if (loading || tourDemo || hubTab !== "championship" || !needsOfficialSync) {
      return undefined;
    }
    const clubKey = `club:${schedule?.club_id || clubId || "x"}:${today}`;
    if (
      autoSyncedFixtures.current.has(clubKey) ||
      autoFlttSyncAlreadyTried(schedule?.club_id || clubId, today)
    ) {
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      if (cancelled || autoSyncedFixtures.current.has(clubKey)) return;
      autoSyncedFixtures.current.add(clubKey);
      markAutoFlttSyncTried(schedule?.club_id || clubId, today);
      setResultsLookup("looking");
      onGetFlttResults({ auto: true, silent: true }).then((data) => {
        if (cancelled) return;
        if (data?.auto_skipped) {
          setResultsLookup((cur) => (cur === "looking" ? "" : cur));
          return;
        }
        const applied = Array.isArray(data?.applied) ? data.applied : [];
        if (!applied.length) {
          setResultsLookup((cur) => (cur === "looking" ? "later" : cur));
        }
      });
    }, 2500);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [
    loading,
    tourDemo,
    hubTab,
    needsOfficialSync,
    schedule?.club_id,
    clubId,
    today,
    onGetFlttResults,
  ]);

  return (
    <main className={`comp-page comp-page--${mode}`}>
      <div className="comp-page__inner">
      <header className="comp-hero" data-tour-id="player-competition">
        <div className="comp-hero__glow" aria-hidden />
        <p className="comp-page__kicker">
          <Trophy size={14} strokeWidth={2.2} aria-hidden />
          {schedule?.season
            ? `${schedule.season.season_label} · ${seasonCategoryLabel}`
            : t("competitionTitle", "Competition")}
        </p>
        <h1>{t("competitionTitle", "Competition")}</h1>
        <p className="comp-hero__lede">
          {showBelgiumCopy
            ? t(
                "competitionHubSubtitleBelgium",
                "Club teams on the Belgian interclub championship — and every other tournament."
              )
            : showGermanyCopy
              ? t(
                  "competitionHubSubtitleGermany",
                  "Club teams on the German interclub championship — and every other tournament."
                )
              : t(
                  "competitionHubSubtitle",
                  "Club teams on the national championship — and every other tournament."
                )}
        </p>
        {showTacticalPatternsLink ? (
          <p>
            <Link to="/tactical" className="comp-hero__schema">
              {t("schemaLibraryTitle", "Tactical patterns")}
            </Link>
          </p>
        ) : null}
        {schedule?.season ? (
          <p className="comp-page__round">{seasonRoundLabel(schedule.season, t)}</p>
        ) : null}

        {!waiting &&
        (clubTeams.length ||
          otherEvents.length ||
          (showFlttEmptyCopy && flttTournaments.length)) ? (
          <ul className="comp-hero__stats">
            {clubTeams.length ? (
              <li>
                <strong>{clubTeams.length}</strong>
                <span>{t("competitionClubTeams", "Club teams")}</span>
              </li>
            ) : null}
            {(showFlttEmptyCopy ? flttTournaments.length : otherEvents.length) ? (
              <li>
                <strong>
                  {showFlttEmptyCopy ? flttTournaments.length : otherEvents.length}
                </strong>
                <span>{t("competitionTournaments", "Tournaments")}</span>
              </li>
            ) : null}
            {nextFixture?.match_date ? (
              <li>
                <strong>{formatMatchDate(nextFixture.match_date, locale)}</strong>
                <span>{t("competitionNextMatch", "Next match")}</span>
              </li>
            ) : null}
            {feltenCup?.still_playing && feltenCup?.next_match?.match_date ? (
              <li>
                <strong>
                  {formatMatchDate(feltenCup.next_match.match_date, locale)}
                </strong>
                <span>{t("competitionFeltenNextMatch", "Next Felten match")}</span>
              </li>
            ) : null}
          </ul>
        ) : null}
      </header>

      {phoneClips.count > 0 ? (
        <Link to="/competition/phone-videos" className="comp-phone-videos">
          {phoneClips.busy ? (
            <CloudUpload size={18} strokeWidth={2.2} aria-hidden />
          ) : (
            <Smartphone size={18} strokeWidth={2.2} aria-hidden />
          )}
          <span className="comp-phone-videos__text">
            <strong>{t("cmPhoneVideosTitle", "Videos on this phone")}</strong>
            <span>
              {phoneClips.busy
                ? clipUploadStatusText(phoneClips, t)
                : t("cmPhoneVideosBanner", "{{n}} match clips waiting to upload", {
                    n: phoneClips.count,
                  })}
            </span>
          </span>
          <ChevronRight size={18} aria-hidden />
        </Link>
      ) : null}

      <nav
        className={`comp-tabs ${showFlttEmptyCopy ? "comp-tabs--with-cups" : ""}`}
        aria-label={t("competitionTitle", "Competition")}
      >
        <button
          type="button"
          className={hubTab === "championship" ? "is-active" : ""}
          aria-current={hubTab === "championship" ? "page" : undefined}
          aria-label={t("competitionChampionship", "Championship")}
          title={t("competitionChampionship", "Championship")}
          onClick={() => setHubTab("championship")}
        >
          <Shield size={16} strokeWidth={2.2} aria-hidden />
          <span className="comp-tabs__text">
            <span className="comp-tabs__full">
              {t("competitionChampionship", "Championship")}
            </span>
            <span className="comp-tabs__abbr" aria-hidden="true">
              {t("competitionChampionshipShort", "Champ")}
            </span>
          </span>
        </button>
        {showFlttEmptyCopy ? (
          <button
            type="button"
            className={hubTab === "cups" ? "is-active" : ""}
            aria-current={hubTab === "cups" ? "page" : undefined}
            aria-label={t("competitionCups", "Cups")}
            title={t("competitionCups", "Cups")}
            onClick={() => setHubTab("cups")}
          >
            <Trophy size={16} strokeWidth={2.2} aria-hidden />
            <span className="comp-tabs__text">
              <span className="comp-tabs__full">
                {t("competitionCups", "Cups")}
              </span>
              <span className="comp-tabs__abbr" aria-hidden="true">
                {t("competitionCupsShort", "Cups")}
              </span>
            </span>
            {feltenCup?.registered && Array.isArray(feltenCup.teams) ? (
              <em>{feltenCup.teams.length}</em>
            ) : null}
          </button>
        ) : null}
        <button
          type="button"
          className={hubTab === "tournaments" ? "is-active" : ""}
          aria-current={hubTab === "tournaments" ? "page" : undefined}
          aria-label={t("competitionTournaments", "Tournaments")}
          title={t("competitionTournaments", "Tournaments")}
          onClick={() => setHubTab("tournaments")}
        >
          <Medal size={16} strokeWidth={2.2} aria-hidden />
          <span className="comp-tabs__text">
            <span className="comp-tabs__full">
              {t("competitionTournaments", "Tournaments")}
            </span>
            <span className="comp-tabs__abbr" aria-hidden="true">
              {t("competitionTournamentsShort", "Tour.")}
            </span>
          </span>
          {(showFlttEmptyCopy ? flttTournaments.length : otherEvents.length) ? (
            <em>{showFlttEmptyCopy ? flttTournaments.length : otherEvents.length}</em>
          ) : null}
        </button>
      </nav>

      {waiting && hubTab === "championship" ? (
        <div className="comp-skel" aria-busy="true" aria-live="polite">
          <span className="comp-skel__bar" />
          <div className="comp-skel__cards">
            <span />
            <span />
            <span />
          </div>
          <span className="comp-skel__hero" />
          <span className="comp-skel__row" />
          <span className="comp-skel__row" />
        </div>
      ) : null}
      {error ? <p className="comp-page__error">{error}</p> : null}

      {!waiting && hubTab === "championship" && empty ? (
        <section className="comp-empty" aria-live="polite" data-tour-id="competition-calendar">
          <div className="comp-empty__icon" aria-hidden>
            <Shield size={28} strokeWidth={1.8} />
          </div>
          <h2>{t("competitionEmptyTitle", "No championship calendar yet")}</h2>
          <p>
            {isCoach
              ? showFlttEmptyCopy
                ? t(
                    "competitionEmptyCoach",
                    "Ask Pingflo to load the FLTT Spielplan — attach the NL 1 National TT League PDF for Team 1 and the Seniors PDF for NL 2 and below."
                  )
                : showFranceEmptyCopy
                  ? t(
                      "competitionEmptyCoachFrance",
                      "Ask Pingflo to load the LGETT championnat calendar — attach the Grand Est poule PDF (GE Elite, GE1–GE7) or paste the official lgett.fr link."
                    )
                  : showBelgiumCopy
                    ? t(
                        "competitionEmptyCoachBelgium",
                        "Ask Pingflo to sync the VTTL / AFTT interclub calendar from TabT when it is available."
                      )
                    : showGermanyCopy
                      ? t(
                          "competitionEmptyCoachGermany",
                          "Ask Pingflo to sync the DTTB interclub calendar from myTischtennis when it is available."
                        )
                      : t(
                          "competitionEmptyCoachGeneric",
                          "Ask Pingflo to load the championship calendar when it is available."
                        )
              : showFlttEmptyCopy
                ? t(
                    "competitionEmptyPlayer",
                    "Your coach will import the official FLTT calendar. Check back soon."
                  )
                : showFranceEmptyCopy
                  ? t(
                      "competitionEmptyPlayerFrance",
                      "Your coach will import the official FFTT / LGETT calendar. Check back soon."
                    )
                  : showBelgiumCopy
                    ? t(
                        "competitionEmptyPlayerBelgium",
                        "Your coach will sync the official VTTL / AFTT interclub calendar. Check back soon."
                      )
                    : showGermanyCopy
                      ? t(
                          "competitionEmptyPlayerGermany",
                          "Your coach will sync the official DTTB interclub calendar. Check back soon."
                        )
                      : t(
                          "competitionEmptyPlayerGeneric",
                          "Your coach will import the official calendar when it is available. Check back soon."
                        )}
          </p>
          {otherEvents.length ? (
            <button
              type="button"
              className="comp-empty__cta"
              onClick={() => setHubTab("tournaments")}
            >
              {t("competitionTournaments", "Tournaments")}
            </button>
          ) : null}
        </section>
      ) : null}

      {!waiting && hubTab === "championship" && !empty ? (
        <div className="comp-championship" data-tour-id="competition-calendar">
          <section className="comp-stage-head" aria-labelledby="comp-national-title">
            <div>
              <p className="comp-stage-head__kicker">
                {showBelgiumCopy
                  ? t("competitionNationalBelgium", "Belgian interclub championship")
                  : showGermanyCopy
                    ? t("competitionNationalGermany", "German interclub championship")
                    : t("competitionNational", "National championship")}
              </p>
              <h2 id="comp-national-title">
                {championshipEvent?.name || seasonCategoryLabel}
              </h2>
            </div>
            <div className="comp-stage-head__actions">
              {canGetFlttResults ? (
                <button
                  type="button"
                  className="comp-get-results"
                  onClick={() => onGetFlttResults({ auto: false, silent: false })}
                  disabled={syncingResults}
                  aria-busy={syncingResults}
                >
                  <Download size={15} strokeWidth={2.2} aria-hidden />
                  {syncingResults
                    ? t("competitionGettingResults", "Getting results…")
                    : t("competitionGetResults", "Get results")}
                </button>
              ) : null}
              <button
                type="button"
                className="comp-browse"
                onClick={() => setPickerOpen(true)}
                aria-expanded={pickerOpen}
              >
                {t("competitionBrowseLeague", "Browse league")}
                <ChevronDown size={16} strokeWidth={2.2} aria-hidden />
              </button>
            </div>
          </section>
          {syncNotice ? (
            <p className="comp-sync-notice" role="status">
              {syncNotice}
            </p>
          ) : null}

          {browseTeam ? (
            <p className="comp-browse-note">
              <ClubCrest team={browseTeam} ownClubId={schedule?.club_id} />
              <span>
                {browseTeam.display_name}
                <em>{browseTeam.division}</em>
              </span>
              <button type="button" onClick={() => onPickTeam(clubTeams[0] || browseTeam)}>
                {t("competitionClubTeams", "Club teams")}
              </button>
            </p>
          ) : null}

          {clubTeams.length ? (
            <div
              className="comp-teams"
              aria-label={t("competitionClubTeams", "Club teams")}
            >
              <Slide
                key={`${teamSlidesToShow}-${clubTeams.length}`}
                slidesToScroll={teamSlidesVisible}
                slidesToShow={teamSlidesVisible}
                indicators={canSlideTeams}
                arrows={false}
                autoplay={false}
                infinite={canSlideTeams}
                canSwipe={canSlideTeams}
                defaultIndex={Math.min(
                  teamSlideDefaultIndex,
                  Math.max(0, clubTeams.length - 1)
                )}
                cssClass="comp-teams-slideshow"
              >
                {clubTeams.map((team) => {
                  const active =
                    !browseTeam && team.league_team_id === selectedTeam?.league_team_id;
                  const mine = playableTeamIds.has(team.league_team_id);
                  const rank = standingsRowForTeam(
                    standingsTableForTeam(standingsTables, team),
                    team
                  )?.rank;
                  return (
                    <div className="comp-teams__slide" key={team.league_team_id}>
                      <button
                        type="button"
                        className={`comp-teams__card ${active ? "is-active" : ""}`}
                        onClick={() => onPickTeam(team)}
                        aria-pressed={active}
                      >
                        <ClubCrest team={team} ownClubId={schedule?.club_id} />
                        <strong>{team.display_name}</strong>
                        <em>{team.division}</em>
                        {rank ? (
                          <span className="comp-teams__rank">#{rank}</span>
                        ) : null}
                        {mine ? (
                          <span className="comp-teams__mine">
                            {t("competitionSquad", "Squad")}
                          </span>
                        ) : null}
                      </button>
                    </div>
                  );
                })}
              </Slide>
            </div>
          ) : (
            <button
              type="button"
              className="comp-team-chip"
              onClick={() => setPickerOpen(true)}
              aria-expanded={pickerOpen}
            >
              <span className="comp-team-chip__main">
                {selectedTeam ? (
                  <ClubCrest team={selectedTeam} ownClubId={schedule?.club_id} />
                ) : null}
                <span>
                  <strong>{selectedTeam?.display_name || t("competitionPickTeam", "Choose a team")}</strong>
                  <em>{selectedTeam?.division || t("competitionAllTeams", "Club teams")}</em>
                </span>
              </span>
              <ChevronDown size={18} strokeWidth={2.2} aria-hidden />
            </button>
          )}

          <nav className="comp-days" aria-label={t("competitionMatchdays", "Matchdays")}>
              <button
                type="button"
                className={isMatchesTab ? "is-active" : ""}
                onClick={() => {
                  setMatchdayFilter(MATCHES_TAB);
                  setActiveFixtureId("");
                }}
              >
                {t("competitionMatches", "Matches")}
              </button>
              <button
                type="button"
                className={!matchdayFilter ? "is-active" : ""}
                onClick={() => setMatchdayFilter("")}
              >
                {t("competitionAll", "All")}
              </button>
              {matchdaysForTeam.map((md) => (
                <button
                  key={md.code}
                  type="button"
                  className={matchdayFilter === md.code ? "is-active" : ""}
                  onClick={() => setMatchdayFilter(md.code)}
                >
                  {md.code}
                </button>
              ))}
            </nav>

          <div className="comp-stage">
            <div className="comp-stage__primary">
            {isMatchesTab && highlightedFixture && selectedTeam ? (
              <section
                className={`comp-next${highlightedScore?.complete ? " has-score is-final" : ""}${highlightedOutcome ? ` is-${highlightedOutcome}` : ""}${highlightedIsPast ? " is-past" : ""}${nextMatchLive ? " is-live" : ""}${waitingOfficialResults ? " is-waiting" : ""}`}
                aria-labelledby="comp-next-title"
                role="button"
                tabIndex={0}
                onClick={(e) =>
                  onMatchCardTap(e, highlightedFixture.fixture_id)
                }
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onMatchCardTap(e, highlightedFixture.fixture_id);
                  }
                }}
              >
                <div className="comp-next__top">
                  <p id="comp-next-title" className="comp-next__label">
                    {highlightedScore?.complete
                      ? t("competitionResult", "Result")
                      : nextMatchLive
                        ? t("cmLive", "Live")
                      : highlightedIsPast
                        ? t("competitionPlayed", "Played")
                      : nextIsHighlighted
                        ? t("competitionNextMatch", "Next match")
                        : t("competitionMatch", "Match")}
                  </p>
                  <div className="comp-next__top-end">
                    {highlightedScore ? (
                      <span
                        className={`comp-next__score-badge${
                          highlightedScore.live || nextMatchLive ? " is-live" : ""
                        }${highlightedScore.complete ? " is-final" : ""}${
                          highlightedOutcome ? ` is-${highlightedOutcome}` : ""
                        }`}
                        aria-label={
                          highlightedScore.complete
                            ? t(
                                "competitionFinalScore",
                                "Final score {{home}}–{{away}}",
                                {
                                  home: highlightedScore.home,
                                  away: highlightedScore.away,
                                }
                              )
                            : t("competitionLiveScore", "Live {{home}}–{{away}}", {
                                home: highlightedScore.home,
                                away: highlightedScore.away,
                              })
                        }
                      >
                        {highlightedScore.live || nextMatchLive ? (
                          <em className="comp-next__live-tag">{t("cmLive", "Live")}</em>
                        ) : null}
                        <strong>
                          {highlightedScore.home}–{highlightedScore.away}
                        </strong>
                        {highlightedScore.hasSets ? (
                          <small className="comp-score-sets">
                            {t("cmSets", "Sets")} {highlightedScore.homeSets}–
                            {highlightedScore.awaySets}
                          </small>
                        ) : null}
                      </span>
                    ) : nextMatchLive ? (
                      <span className="comp-next__score-badge is-live" aria-live="polite">
                        <em className="comp-next__live-tag">{t("cmLive", "Live")}</em>
                      </span>
                    ) : relative && !highlightedIsPast ? (
                      <span className="comp-next__when">{relative}</span>
                    ) : highlightedIsPast && !highlightedScore?.complete ? (
                      <span className="comp-next__when is-past">
                        {t("competitionPlayed", "Played")}
                      </span>
                    ) : null}
                    {!browseTeam &&
                    highlightedFixture &&
                    !highlightedIsPast &&
                    !highlightedScore?.complete ? (
                      <button
                        type="button"
                        className={`comp-next__rec${nextMatchLive ? " is-live" : ""}`}
                        disabled={busy}
                        onClick={() =>
                          openLiveMatch(highlightedFixture.fixture_id, {
                            tab: nextMatchLive || !highlightedScore?.complete ? "camera" : undefined,
                          })
                        }
                        aria-label={nextRecLabel}
                        title={nextRecLabel}
                      >
                        <Video size={18} strokeWidth={2.2} aria-hidden />
                      </button>
                    ) : null}
                  </div>
                </div>
                <div className="comp-next__meta">
                  <CalendarDays size={16} strokeWidth={2.1} aria-hidden />
                  <span>
                    {formatMatchDate(highlightedFixture.match_date, locale)}
                    {highlightedFixture.kickoff_time ? ` · ${highlightedFixture.kickoff_time}` : ""}
                  </span>
                  <span className="comp-dot" aria-hidden />
                  <span>{highlightedFixture.matchday?.code}</span>
                </div>
                <div
                  className="comp-next__matchup"
                  role="group"
                  aria-label={`${teamSideLabels(highlightedFixture.home, t).club} vs ${teamSideLabels(highlightedFixture.away, t).club}`}
                >
                  {["home", "away"].flatMap((side, idx) => {
                    const team =
                      side === "home"
                        ? highlightedFixture.home
                        : highlightedFixture.away;
                    const labels = teamSideLabels(team, t);
                    const ours = isHomeForTeam(highlightedFixture, selectedTeam)
                      ? side === "home"
                      : side === "away";
                    const showOppHere = showOpponentPreview && !ours;
                    const showOurLineupHere =
                      ours &&
                      !browseTeam &&
                      (showStoredLineup
                        ? ourOfficialLineup.length > 0
                        : matchSquadPlayers.length > 0);
                    const ourLineupPlayers = showStoredLineup
                      ? ourOfficialLineup
                      : matchSquadPlayers;
                    const sideNode = (
                      <div
                        key={side}
                        className={`comp-next__side${ours ? " is-ours" : " is-opp"}`}
                      >
                        <ClubCrest
                          team={team}
                          size="md"
                          ownClubId={schedule?.club_id}
                          onClick={
                            clubCardPayload(team)
                              ? (e) => {
                                  e.stopPropagation();
                                  openClubCard(team);
                                }
                              : null
                          }
                        />
                        <span className="comp-next__names">
                          <strong className="comp-next__club-name">
                            {labels.club}
                          </strong>
                          {labels.team ? (
                            <em className="comp-next__team-name">{labels.team}</em>
                          ) : null}
                        </span>
                        {showOurLineupHere ? (
                          <div className="comp-lineup comp-lineup--side">
                            <p className="comp-lineup__label">
                              <Users size={12} strokeWidth={2.2} aria-hidden />
                              {t("competitionLineup", "Line-up")}
                            </p>
                            <ul className="comp-lineup__side-list">
                              {ourLineupPlayers.map((p) => {
                                const name = p.name || p.display_name || "—";
                                const st = showStoredLineup
                                  ? "in"
                                  : rsvps.find(
                                      (r) =>
                                        String(r.user_id) === String(p.user_id)
                                    )?.status || "pending";
                                return (
                                  <li
                                    key={
                                      p.user_id ||
                                      p.player_id ||
                                      p.seat ||
                                      name
                                    }
                                    className={`is-${st}`}
                                    title={
                                      showStoredLineup
                                        ? name
                                        : `${name} · ${st}`
                                    }
                                  >
                                    {p.user_image ? (
                                      <img src={p.user_image} alt="" />
                                    ) : (
                                      <span className="comp-lineup__initials">
                                        {initials(name)}
                                      </span>
                                    )}
                                    <strong>{name}</strong>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        ) : null}
                        {showOppHere ? (
                          <OpponentPreviewBlock
                            preview={
                              showStoredLineup
                                ? {
                                    ...opponentPreview,
                                    extras: [],
                                  }
                                : opponentPreview
                            }
                            canSet={canSetOpponentAvailability}
                            busy={busy}
                            onSetAvailability={onSetOpponentAvailability}
                            t={t}
                            embedded
                            showExtras={!showStoredLineup}
                            memoryNotes={
                              showStoredLineup ? [] : opponentMemoryNotes
                            }
                          />
                        ) : null}
                      </div>
                    );
                    if (idx === 0) {
                      return [
                        sideNode,
                        <span key="vs" className="comp-next__vs">
                          {t("competitionVs", "vs")}
                        </span>,
                      ];
                    }
                    return [sideNode];
                  })}
                </div>
                {showOpponentPreview && canSetOpponentAvailability ? (
                  <p className="comp-opp-preview__hint">
                    {t(
                      "competitionOpponentMatchDayHint",
                      "On match day you can set who plays or sits until the official team is posted."
                    )}
                  </p>
                ) : null}
                <p className="comp-next__venue">
                  <span
                    className={
                      isHomeForTeam(highlightedFixture, selectedTeam)
                        ? "pill pill--home"
                        : "pill pill--away"
                    }
                  >
                    {isHomeForTeam(highlightedFixture, selectedTeam)
                      ? t("competitionHome", "Home")
                      : t("competitionAway", "Away")}
                  </span>
                  <span className="comp-next__div">
                    <MapPin size={14} strokeWidth={2.2} aria-hidden />
                    {highlightedFixture.division}
                  </span>
                </p>

                {waitingOfficialResults ? (
                  <p
                    className={`comp-results-note${resultsLookup === "later" ? " is-later" : ""}`}
                    role="status"
                  >
                    {resultsLookup === "later"
                      ? t(
                          "competitionResultsLater",
                          "Results are not published yet. Try again later."
                        )
                      : t(
                          "competitionResultsLooking",
                          "Looking up official results…"
                        )}
                  </p>
                ) : null}

                {canRsvp ? (
                  <div className="comp-rsvp" role="group" aria-label={t("competitionRsvp", "Confirm participation")}>
                    <button
                      type="button"
                      className={`comp-rsvp__btn ${myRsvp === "in" ? "is-in" : ""}`}
                      disabled={busy}
                      onClick={() => onRsvp("in")}
                    >
                      {t("competitionIllPlay", "I’ll play")}
                    </button>
                    <button
                      type="button"
                      className={`comp-rsvp__btn ${myRsvp === "out" ? "is-out" : ""}`}
                      disabled={busy}
                      onClick={() => onRsvp("out")}
                    >
                      {t("competitionCantPlay", "I can’t")}
                    </button>
                  </div>
                ) : null}

                {canManagePresence &&
                !presenceLocked &&
                (matchSquadPlayers.length ||
                  matchAbsentPlayers.length ||
                  matchAlternatePlayers.length) ? (
                  <div className="comp-presence-block">
                    <p className="comp-presence__label">
                      {t("competitionSetPresence", "Match presence")}
                    </p>
                    <ul
                      className="comp-presence"
                      aria-label={t("competitionSetPresence", "Match presence")}
                    >
                      {matchSquadPlayers.map((p) => {
                        const st =
                          rsvps.find((r) => String(r.user_id) === String(p.user_id))
                            ?.status || "pending";
                        const cls =
                          p.classement || p.fltt_classement || "";
                        const place = formatRankingPlace(
                          p.ranking_place ?? p.fltt_ranking_place
                        );
                        return (
                          <li
                            key={p.user_id}
                            className={`comp-presence__row is-${st}`}
                          >
                            <span className="comp-presence__who">
                              {p.user_image ? (
                                <img src={p.user_image} alt="" />
                              ) : (
                                <span className="comp-presence__ini">
                                  {initials(p.name)}
                                </span>
                              )}
                              <strong>{p.name}</strong>
                              {cls ? (
                                <em className="comp-presence__cls">{cls}</em>
                              ) : null}
                              {place ? (
                                <em className="comp-presence__cls">{place}</em>
                              ) : null}
                            </span>
                            <span className="comp-presence__acts" role="group">
                              <button
                                type="button"
                                className={st === "in" ? "is-in" : ""}
                                disabled={busy || !highlightedFixture}
                                title={t("competitionPresent", "Play")}
                                onClick={() =>
                                  onSetPlayerRsvp(
                                    p.user_id,
                                    st === "in" ? "pending" : "in"
                                  )
                                }
                              >
                                {t("competitionPresent", "Play")}
                              </button>
                              <button
                                type="button"
                                className={st === "out" ? "is-out" : ""}
                                disabled={busy || !highlightedFixture}
                                title={t("competitionAbsent", "Absent")}
                                onClick={() =>
                                  onSetPlayerRsvp(p.user_id, "out")
                                }
                              >
                                {t("competitionAbsent", "Absent")}
                              </button>
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                    {matchAlternatePlayers.length ? (
                      <div className="comp-presence-phase">
                        <p className="comp-presence__label">
                          {t(
                            "competitionSameClassAlternates",
                            "Same classement — alternatives"
                          )}
                        </p>
                        <p className="comp-presence__hint">
                          {t(
                            "competitionSameClassAlternatesHint",
                            "Same classement only. Points must not exceed the absent player; no superior-division history; not already selected on a higher team. A replaced player can play the next team."
                          )}
                        </p>
                        <ul
                          className="comp-presence"
                          aria-label={t(
                            "competitionSameClassAlternates",
                            "Same classement — alternatives"
                          )}
                        >
                          {matchAlternatePlayers.map((p) => {
                            const cls =
                              p.classement || p.fltt_classement || "";
                            const place = formatRankingPlace(
                              p.ranking_place ?? p.fltt_ranking_place
                            );
                            return (
                              <li
                                key={p.user_id}
                                className="comp-presence__row is-alt"
                              >
                                <span className="comp-presence__who">
                                  {p.user_image ? (
                                    <img src={p.user_image} alt="" />
                                  ) : (
                                    <span className="comp-presence__ini">
                                      {initials(p.name)}
                                    </span>
                                  )}
                                  <strong>{p.name}</strong>
                                  {cls ? (
                                    <em className="comp-presence__cls">
                                      {cls}
                                    </em>
                                  ) : null}
                                  {place ? (
                                    <em className="comp-presence__cls">
                                      {place}
                                    </em>
                                  ) : null}
                                </span>
                                <span
                                  className="comp-presence__acts"
                                  role="group"
                                >
                                  <button
                                    type="button"
                                    className="is-in"
                                    disabled={busy || !highlightedFixture}
                                    title={t(
                                      "competitionSelectForTeam",
                                      "Select for this team"
                                    )}
                                    onClick={() => onSelectAlternate(p)}
                                  >
                                    {t(
                                      "competitionSelectForTeam",
                                      "Select"
                                    )}
                                  </button>
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ) : null}
                    {matchAbsentPlayers.length ? (
                      <div className="comp-presence-phase">
                        <p className="comp-presence__label">
                          {t("competitionAbsentThisMatch", "Absent this match")}
                        </p>
                        <ul
                          className="comp-presence"
                          aria-label={t(
                            "competitionAbsentThisMatch",
                            "Absent this match"
                          )}
                        >
                          {matchAbsentPlayers.map((p) => (
                            <li
                              key={p.user_id}
                              className="comp-presence__row is-out"
                            >
                              <span className="comp-presence__who">
                                {p.user_image ? (
                                  <img src={p.user_image} alt="" />
                                ) : (
                                  <span className="comp-presence__ini">
                                    {initials(p.name)}
                                  </span>
                                )}
                                <strong>{p.name}</strong>
                              </span>
                              <span className="comp-presence__acts" role="group">
                                <button
                                  type="button"
                                  disabled={busy || !highlightedFixture}
                                  title={t(
                                    "competitionRestorePresence",
                                    "Restore"
                                  )}
                                  onClick={() =>
                                    onSetPlayerRsvp(p.user_id, "pending")
                                  }
                                >
                                  {t("competitionRestorePresence", "Restore")}
                                </button>
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </div>
                ) : null}

              </section>
            ) : isMatchesTab ? (
              <p className="comp-page__status">
                {t("competitionNoFixtures", "No matches for this team yet.")}
              </p>
            ) : null}

            {isMatchesTab && showNextChampionshipTeaser ? (
              <section
                className="comp-tour-inscribe"
                aria-labelledby="comp-next-champ-title"
              >
                <div className="comp-tour-inscribe__head">
                  <p className="comp-tour-inscribe__kicker">
                    <Trophy size={14} strokeWidth={2.2} aria-hidden />
                    {t("competitionNextMatch", "Next match")}
                  </p>
                  <button
                    type="button"
                    className="comp-tour-inscribe__open"
                    onClick={() =>
                      setActiveFixtureId(upcomingChampionshipFixture.fixture_id)
                    }
                  >
                    {t("competitionOpenMatch", "Open")}
                  </button>
                </div>
                <h3 id="comp-next-champ-title">
                  {`${teamSideLabels(upcomingChampionshipFixture.home, t).club} ${t(
                    "competitionVs",
                    "vs"
                  )} ${teamSideLabels(upcomingChampionshipFixture.away, t).club}`}
                </h3>
                <p className="comp-tour-inscribe__meta">
                  {upcomingChampionshipFixture.match_date ? (
                    <time
                      className="comp-tour-inscribe__date"
                      dateTime={upcomingChampionshipFixture.match_date}
                    >
                      {[
                        formatMatchDate(
                          upcomingChampionshipFixture.match_date,
                          locale
                        ),
                        upcomingChampionshipFixture.kickoff_time || null,
                        relativeMatchLabel(
                          upcomingChampionshipFixture.match_date,
                          t
                        ),
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </time>
                  ) : null}
                  {upcomingChampionshipFixture.matchday?.code
                    ? ` · ${upcomingChampionshipFixture.matchday.code}`
                    : ""}
                  {upcomingChampionshipFixture.division
                    ? ` · ${upcomingChampionshipFixture.division}`
                    : ""}
                </p>
              </section>
            ) : null}
            </div>

            {!isMatchesTab ? (
            <section className="comp-list" aria-label={t("competitionSeason", "Season")}>
              <h3 className="comp-list__title">{t("competitionFixtures", "Fixtures")}</h3>
              {groupedFixtures.map(([code, rows]) => (
                <div key={code} className="comp-list__group">
                  <p className="comp-list__md">{code}</p>
                  {rows.map((f) => {
                    const home = isHomeForTeam(f, selectedTeam);
                    const opp = opponentOf(f, selectedTeam);
                    const active = highlightedFixture?.fixture_id === f.fixture_id;
                    const past = fixtureIsPast(f.match_date, today);
                    const liveRow = matchByFixture.get(String(f.fixture_id));
                    const score = scoreForFixture(f, liveRow);
                    const waitingRow = past && !score?.complete && !score?.live;
                    const outcome = clubMatchOutcome(score, f, selectedTeam);
                    return (
                      <article
                        key={f.fixture_id}
                        className={`comp-row ${active ? "is-active" : ""} ${past ? "is-past" : ""}${score?.complete ? " has-score is-final" : ""}${score?.live || liveRow?.status === "live" ? " is-live" : ""}${outcome ? ` is-${outcome}` : ""}${waitingRow ? " is-waiting" : ""}`}
                        onClick={() => {
                          setActiveFixtureId(f.fixture_id);
                          setMatchdayFilter(MATCHES_TAB);
                          if (!browseTeam) void openLiveMatch(f.fixture_id);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setActiveFixtureId(f.fixture_id);
                            if (!browseTeam) void openLiveMatch(f.fixture_id);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-pressed={active}
                        aria-label={
                          score
                            ? `${opp?.display_name || ""} ${score.home}–${score.away}`
                            : undefined
                        }
                      >
                        {score ? (
                          <span
                            className={`comp-row__score-corner${
                              score.live ? " is-live" : ""
                            }${score.complete ? " is-final" : ""}${
                              outcome ? ` is-${outcome}` : ""
                            }`}
                            aria-label={
                              score.complete
                                ? t(
                                    "competitionFinalScore",
                                    "Final score {{home}}–{{away}}",
                                    { home: score.home, away: score.away }
                                  )
                                : t(
                                    "competitionLiveScore",
                                    "Live {{home}}–{{away}}",
                                    { home: score.home, away: score.away }
                                  )
                            }
                          >
                            {score.live ? (
                              <em className="comp-row__live-tag">
                                {t("cmLive", "Live")}
                              </em>
                            ) : null}
                            <strong>
                              {score.home}–{score.away}
                            </strong>
                            {score.hasSets ? (
                              <small className="comp-score-sets">
                                {t("cmSets", "Sets")} {score.homeSets}–
                                {score.awaySets}
                              </small>
                            ) : null}
                          </span>
                        ) : liveRow?.status === "live" ? (
                          <span className="comp-row__score-corner is-live" aria-live="polite">
                            <em className="comp-row__live-tag">
                              {t("cmLive", "Live")}
                            </em>
                          </span>
                        ) : waitingRow ? (
                          <span className="comp-row__score-corner is-waiting">
                            {t("competitionWaitingResults", "Waiting")}
                          </span>
                        ) : null}
                        <div className="comp-row__when">
                          <strong>{formatMatchDate(f.match_date, locale)}</strong>
                          <span>{f.kickoff_time || f.matchday?.code}</span>
                        </div>
                        <div className="comp-row__body">
                          <span className={home ? "pill pill--home" : "pill pill--away"}>
                            {home ? t("competitionHome", "Home") : t("competitionAway", "Away")}
                          </span>
                          <p className="comp-row__name">
                            <ClubCrest team={opp} ownClubId={schedule?.club_id} />
                            <span>{opp?.display_name}</span>
                          </p>
                          <small>{f.division}</small>
                        </div>
                      </article>
                    );
                  })}
                </div>
              ))}
            </section>
            ) : null}
            </div>

          {selectedStandings?.rows?.length ? (
            <section
              className="comp-ladder"
              aria-labelledby="comp-classement-title"
            >
              <header className="comp-ladder__head">
                <div className="comp-ladder__head-copy">
                  <p className="comp-ladder__kicker">
                    <Medal size={14} strokeWidth={2.2} aria-hidden />
                    {t("competitionClassement", "Classement")}
                  </p>
                  <h3 id="comp-classement-title">
                    {selectedStandings.division || selectedTeam?.division}
                  </h3>
                  <p className="comp-ladder__lede">
                    {t(
                      "competitionClassementLede",
                      "League table — points from matches won, drawn, and lost."
                    )}
                  </p>
                </div>
                {selectedStandingsRow ? (
                  <p className="comp-ladder__place-chip" aria-hidden="true">
                    <span>
                      {t("competitionClassementPosShort", "Pos")}
                    </span>
                    <strong>{selectedStandingsRow.rank}</strong>
                    <em>
                      / {selectedStandings.rows.length}
                    </em>
                  </p>
                ) : null}
              </header>

              {selectedStandingsRow ? (
                <div className="comp-ladder__us" aria-current="true">
                  <div className="comp-ladder__us-rank" aria-hidden="true">
                    <span>{selectedStandingsRow.rank}</span>
                    <small>
                      {t("competitionClassementPosShort", "Pos")}
                    </small>
                  </div>
                  <div className="comp-ladder__us-meta">
                    <span className="comp-ladder__us-tag">
                      {t("competitionClassementOurTeam", "Your team")}
                    </span>
                    <strong>{selectedTeam?.display_name}</strong>
                    <em>
                      {t(
                        "competitionClassementForm",
                        "{{played}} played · {{won}}W {{drawn}}D {{lost}}L",
                        {
                          played: selectedStandingsRow.played,
                          won: selectedStandingsRow.won,
                          drawn: selectedStandingsRow.drawn,
                          lost: selectedStandingsRow.lost,
                        }
                      )}
                    </em>
                  </div>
                  <dl className="comp-ladder__us-stats">
                    <div className="is-win">
                      <dt>{t("competitionClassementWonShort", "W")}</dt>
                      <dd>{selectedStandingsRow.won}</dd>
                    </div>
                    <div className="is-draw">
                      <dt>{t("competitionClassementDrawnShort", "D")}</dt>
                      <dd>{selectedStandingsRow.drawn}</dd>
                    </div>
                    <div className="is-loss">
                      <dt>{t("competitionClassementLostShort", "L")}</dt>
                      <dd>{selectedStandingsRow.lost}</dd>
                    </div>
                    <div className="is-pts">
                      <dt>{t("competitionClassementPts", "pts")}</dt>
                      <dd>{selectedStandingsRow.points}</dd>
                    </div>
                  </dl>
                </div>
              ) : null}

              <div className="comp-ladder__board">
                <table className="comp-ladder__table">
                  <caption className="comp-ladder__caption">
                    {t(
                      "competitionClassementCaption",
                      "Standings for {{division}}",
                      {
                        division:
                          selectedStandings.division ||
                          selectedTeam?.division ||
                          t("competitionClassement", "Classement"),
                      }
                    )}
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col" className="comp-ladder__th-pos">
                        <abbr
                          title={t(
                            "competitionClassementPosDesc",
                            "Position in the league table"
                          )}
                        >
                          {t("competitionClassementPosShort", "Pos")}
                        </abbr>
                      </th>
                      <th scope="col" className="comp-ladder__th-team">
                        <span>
                          {t("competitionClassementTeam", "Team")}
                        </span>
                      </th>
                      <th scope="col" className="comp-ladder__th-num">
                        <abbr
                          title={t(
                            "competitionClassementPlayedDesc",
                            "Matches played"
                          )}
                        >
                          {t("competitionClassementPlayedShort", "P")}
                        </abbr>
                      </th>
                      <th scope="col" className="comp-ladder__th-num is-win">
                        <abbr
                          title={t(
                            "competitionClassementWonDesc",
                            "Matches won"
                          )}
                        >
                          {t("competitionClassementWonShort", "W")}
                        </abbr>
                      </th>
                      <th scope="col" className="comp-ladder__th-num is-draw">
                        <abbr
                          title={t(
                            "competitionClassementDrawnDesc",
                            "Matches drawn"
                          )}
                        >
                          {t("competitionClassementDrawnShort", "D")}
                        </abbr>
                      </th>
                      <th scope="col" className="comp-ladder__th-num is-loss">
                        <abbr
                          title={t(
                            "competitionClassementLostDesc",
                            "Matches lost"
                          )}
                        >
                          {t("competitionClassementLostShort", "L")}
                        </abbr>
                      </th>
                      <th scope="col" className="comp-ladder__th-pts">
                        <abbr
                          title={t(
                            "competitionClassementPtsDesc",
                            "Points from wins, draws, and losses"
                          )}
                        >
                          {t("competitionClassementPtsShort", "Pts")}
                        </abbr>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedStandings.rows.map((row) => {
                      const isUs =
                        fold(row.team_name) ===
                        fold(selectedTeam?.display_name);
                      return (
                        <tr
                          key={`${row.rank}-${row.team_name}`}
                          className={`comp-ladder__row${
                            isUs ? " is-us" : ""
                          }${
                            row.rank <= 3 && !isUs ? " is-podium" : ""
                          }`}
                          aria-current={isUs ? "true" : undefined}
                        >
                          <td className="comp-ladder__pos">
                            <span>{row.rank}</span>
                          </td>
                          <td className="comp-ladder__name">
                            <span>{row.team_name}</span>
                            {isUs ? (
                              <em>
                                {t(
                                  "competitionClassementOurTeam",
                                  "Your team"
                                )}
                              </em>
                            ) : null}
                          </td>
                          <td className="comp-ladder__num">{row.played}</td>
                          <td className="comp-ladder__num is-win">
                            {row.won}
                          </td>
                          <td className="comp-ladder__num is-draw">
                            {row.drawn}
                          </td>
                          <td className="comp-ladder__num is-loss">
                            {row.lost}
                          </td>
                          <td className="comp-ladder__pts">{row.points}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <ul className="comp-ladder__legend" aria-label={t(
                "competitionClassementColsLegend",
                "Column meanings"
              )}>
                <li>
                  <strong>{t("competitionClassementPosShort", "Pos")}</strong>
                  <span>
                    {t(
                      "competitionClassementPosDesc",
                      "Position in the league table"
                    )}
                  </span>
                </li>
                <li>
                  <strong>{t("competitionClassementPlayedShort", "P")}</strong>
                  <span>
                    {t(
                      "competitionClassementPlayedDesc",
                      "Matches played"
                    )}
                  </span>
                </li>
                <li>
                  <strong className="is-win">
                    {t("competitionClassementWonShort", "W")}
                  </strong>
                  <span>
                    {t("competitionClassementWonDesc", "Matches won")}
                  </span>
                </li>
                <li>
                  <strong className="is-draw">
                    {t("competitionClassementDrawnShort", "D")}
                  </strong>
                  <span>
                    {t("competitionClassementDrawnDesc", "Matches drawn")}
                  </span>
                </li>
                <li>
                  <strong className="is-loss">
                    {t("competitionClassementLostShort", "L")}
                  </strong>
                  <span>
                    {t("competitionClassementLostDesc", "Matches lost")}
                  </span>
                </li>
                <li>
                  <strong>
                    {t("competitionClassementPtsShort", "Pts")}
                  </strong>
                  <span>
                    {t(
                      "competitionClassementPtsDesc",
                      "Points from wins, draws, and losses"
                    )}
                  </span>
                </li>
              </ul>
            </section>
          ) : null}
        </div>
      ) : null}

      {hubTab === "cups" && showFlttEmptyCopy ? (
        <section className="comp-events" aria-labelledby="comp-cups-title">
          <header className="comp-events__head">
            <div className="comp-events__head-row">
              <div>
                <h2 id="comp-cups-title">{t("competitionCups", "Cups")}</h2>
                <p>
                  {t(
                    "competitionCupsLede",
                    "Official FLTT team cups — matches, group tables, and feuille lineups."
                  )}
                </p>
              </div>
            </div>
          </header>
          <Suspense fallback={<p className="comp-muted">{t("loading", "Loading…")}</p>}>
          <FlttFeltenCupPanel
            cup={feltenCup}
            loading={feltenLoading}
            error={feltenError}
            canSync={isCoach}
            syncing={feltenSyncing || feltenLoading}
            onSync={onSyncFeltenCup}
            onLoad={() => loadFeltenCup({ refresh: Boolean(isCoach) })}
            canRecord={Boolean(isCoach || canManagePresence)}
            recording={busy}
            onRecordMatch={onRecordFeltenMatch}
            onOpenMatch={onRecordFeltenMatch}
            onOpenScorecam={onScorecamFeltenMatch}
            onPhotographSheet={onPhotographFeltenSheet}
            locale={locale}
            embedded
          />
          </Suspense>
        </section>
      ) : null}

      {hubTab === "tournaments" ? (
        <>
        <ClubTournamentBoard />
        <section className="comp-events" aria-labelledby="comp-events-title">
          <header className="comp-events__head">
            <div className="comp-events__head-row">
              <div>
                <h2 id="comp-events-title">{t("competitionTournaments", "Tournaments")}</h2>
                <p>
                  {showFlttEmptyCopy
                    ? t(
                        "competitionFlttTournamentsLede",
                        "FLTT events from fltt.lu — pick a category and notify your coach."
                      )
                    : t(
                        "competitionTournamentsLede",
                        "Cups and individual FLTT events. Championship matches live on the Championship tab."
                      )}
                </p>
              </div>
            </div>
          </header>

          {flttError ? <p className="comp-page__error">{flttError}</p> : null}

          {showFlttEmptyCopy ? (
            <Suspense fallback={<p className="comp-muted">{t("loading", "Loading…")}</p>}>
            <FlttStdTournamentResults
              currentUserId={user?.user_id || null}
              locale={locale}
              tournaments={flttTournaments}
              canModerateGallery={isCoach}
            />
            </Suspense>
          ) : null}

          {showFlttEmptyCopy ? (
            flttLoading && !flttTournaments.length ? (
              <p className="comp-muted">{t("loading", "Loading…")}</p>
            ) : scheduledFlttTournaments.length ? (
              <>
                <h3 className="comp-events__section-title">
                  {t("competitionScheduledTournaments", "Scheduled")}
                </h3>
                <ul className="comp-events__grid">
                  {scheduledFlttTournaments.map((event) => {
                  const open = expandedTournamentId === event.tournament_id;
                  const allCats = event.categories || [];
                  const eligibleCats = allCats.filter(
                    (c) => c.eligible !== false
                  );
                  const cats = eligibleCats;
                  const myIn = (event.my_rsvps || []).filter((r) => r.status === "in");
                  const myPending = (event.my_rsvps || []).filter(
                    (r) => r.status === "pending"
                  );
                  const selected = new Set(selectedCategories[event.tournament_id] || []);
                  const participants = event.participants || [];
                  const shownParticipants = participants.filter(
                    (p) => p.status === "in" || p.status === "pending"
                  );
                  const playerClassement = event.player_classement || null;
                  const dateParts = tournamentDateParts(event.event_date, locale);
                  const whenLabel = relativeMatchLabel(event.event_date, t);
                  return (
                    <li key={event.tournament_id}>
                      <article
                        className={`comp-event-card comp-event-card--fltt kind-tournament ${open ? "is-open" : ""}`}
                      >
                        <button
                          type="button"
                          className="comp-fltt-card__toggle"
                          aria-expanded={open}
                          onClick={() => {
                            setExpandedTournamentId((id) =>
                              id === event.tournament_id ? "" : event.tournament_id
                            );
                            if (!selected.size && (myIn.length || myPending.length)) {
                              setSelectedCategories((prev) => ({
                                ...prev,
                                [event.tournament_id]: [
                                  ...myIn,
                                  ...myPending,
                                ]
                                  .map((r) => r.category_id)
                                  .filter(Boolean),
                              }));
                            }
                          }}
                        >
                          {dateParts ? (
                            <time
                              className="comp-fltt-card__date"
                              dateTime={event.event_date}
                              aria-label={formatMatchDate(event.event_date, locale)}
                            >
                              <span className="comp-fltt-card__date-weekday">
                                {dateParts.weekday}
                              </span>
                              <strong className="comp-fltt-card__date-day">
                                {dateParts.day}
                              </strong>
                              <span className="comp-fltt-card__date-month">
                                {dateParts.month}
                              </span>
                            </time>
                          ) : (
                            <span className="comp-event-card__icon" aria-hidden>
                              <Medal size={22} strokeWidth={1.8} />
                            </span>
                          )}
                          <span className="comp-event-card__body">
                            <strong>{event.name}</strong>
                            <em>
                              {whenLabel ? (
                                <span className="comp-fltt-card__when">{whenLabel}</span>
                              ) : !dateParts && event.month_label ? (
                                <span className="comp-fltt-card__when">
                                  {event.month_label}
                                </span>
                              ) : null}
                              {(whenLabel || (!dateParts && event.month_label)) &&
                              (cats.length || myIn.length || myPending.length)
                                ? " · "
                                : null}
                              {cats.length
                                ? `${cats.length} ${t("competitionCategories", "categories")}`
                                : null}
                              {cats.length && (myIn.length || myPending.length)
                                ? " · "
                                : null}
                              {myIn.length
                                ? t("competitionYouIn", "You’re in")
                                : myPending.length
                                  ? t("competitionYouMaybe", "Maybe")
                                  : null}
                            </em>
                          </span>
                          <span className="comp-fltt-card__meta">
                            {isCoach && shownParticipants.length ? (
                              <span className="pill pill--home">
                                <Users size={12} aria-hidden /> {shownParticipants.length}
                              </span>
                            ) : null}
                            <ChevronDown
                              size={18}
                              aria-hidden
                              className={open ? "is-rot" : ""}
                            />
                          </span>
                        </button>
                        {event.venue ? (
                          <a
                            className="comp-fltt-card__venue"
                            href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.venue)}`}
                            target="_blank"
                            rel="noreferrer"
                            title={t("competitionOpenInMaps", "Open in Google Maps")}
                          >
                            <MapPin size={12} aria-hidden />
                            <span>{event.venue}</span>
                          </a>
                        ) : null}

                        {open ? (
                          <div className="comp-fltt-card__detail">
                            {event.ball ? (
                              <p className="comp-fltt-card__ball">
                                {t("competitionTournamentBall", "Ball")}: {event.ball}
                              </p>
                            ) : null}
                            {event.notes ? (
                              <p className="comp-fltt-card__notes">{event.notes}</p>
                            ) : null}

                            {cats.length ? (
                              <fieldset className="comp-fltt-cats">
                                <legend>
                                  {playerClassement
                                    ? t(
                                        "competitionPickCategoriesForYou",
                                        "Your categories ({{classement}})",
                                        { classement: playerClassement }
                                      )
                                    : t(
                                        "competitionPickCategories",
                                        "Categories / schedule"
                                      )}
                                </legend>
                                {cats.map((cat) => {
                                  const checked = selected.has(cat.category_id);
                                  const already = myIn.some(
                                    (r) => r.category_id === cat.category_id
                                  );
                                  return (
                                    <label
                                      key={cat.category_id}
                                      className={`comp-fltt-cat ${already ? "is-in" : ""}`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() =>
                                          toggleCategorySelection(
                                            event.tournament_id,
                                            cat.category_id
                                          )
                                        }
                                      />
                                      <span className="comp-fltt-cat__time">
                                        {cat.start_time || "—"}
                                      </span>
                                      <span className="comp-fltt-cat__code">{cat.code}</span>
                                      {already ? (
                                        <em>{t("competitionYouIn", "You’re in")}</em>
                                      ) : myPending.some(
                                          (r) => r.category_id === cat.category_id
                                        ) ? (
                                        <em>{t("competitionYouMaybe", "Maybe")}</em>
                                      ) : null}
                                    </label>
                                  );
                                })}
                              </fieldset>
                            ) : allCats.length ? (
                              <p className="comp-muted">
                                {playerClassement
                                  ? t(
                                      "competitionNoEligibleCategories",
                                      "No category matches your classement ({{classement}}).",
                                      { classement: playerClassement }
                                    )
                                  : t(
                                      "competitionNeedClassement",
                                      "Set your FLTT classement on your profile to see categories you can enter."
                                    )}
                              </p>
                            ) : (
                              <p className="comp-muted">
                                {t(
                                  "competitionNoCategoriesYet",
                                  "Category schedule not published yet — you can still notify interest for the whole event."
                                )}
                              </p>
                            )}

                            <div className="comp-rsvp" role="group">
                              <button
                                type="button"
                                className={`comp-rsvp__btn ${myIn.length ? "is-in" : ""}`}
                                disabled={
                                  busy ||
                                  (allCats.length > 0 && eligibleCats.length === 0)
                                }
                                onClick={() => onNotifyTournamentParticipation(event, "in")}
                              >
                                {t("competitionNotifyParticipation", "Notify participation")}
                              </button>
                              <button
                                type="button"
                                className={`comp-rsvp__btn ${myPending.length && !myIn.length ? "is-maybe" : ""}`}
                                disabled={
                                  busy ||
                                  (allCats.length > 0 && eligibleCats.length === 0)
                                }
                                onClick={() =>
                                  onNotifyTournamentParticipation(event, "pending")
                                }
                              >
                                {t("competitionMaybe", "Maybe")}
                              </button>
                              {myIn.length || myPending.length ? (
                                <button
                                  type="button"
                                  className="comp-rsvp__btn is-out"
                                  disabled={busy}
                                  onClick={() =>
                                    onNotifyTournamentParticipation(event, "out")
                                  }
                                >
                                  {t("competitionWithdraw", "Withdraw")}
                                </button>
                              ) : null}
                              {myIn.map((rsvp) => {
                                const cat = allCats.find(
                                  (item) => item.category_id === rsvp.category_id
                                );
                                return (
                                  <React.Fragment key={rsvp.category_id || "event"}>
                                    <button
                                      type="button"
                                      className="comp-rsvp__btn is-in"
                                      disabled={busy}
                                      onClick={() =>
                                        onRecordTournamentMatch(event, rsvp.category_id)
                                      }
                                    >
                                      {cat?.code
                                        ? t("ptmRecordCategory", "Record {{code}}", {
                                            code: cat.code,
                                          })
                                        : t("ptmRecordMatch", "Record my match")}
                                    </button>
                                    <button
                                      type="button"
                                      className="comp-rsvp__btn is-in"
                                      disabled={busy}
                                      onClick={() =>
                                        onRecordTournamentMatch(event, rsvp.category_id, {
                                          scorecam: true,
                                        })
                                      }
                                    >
                                      <ScanLine size={14} aria-hidden />
                                      {cat?.code
                                        ? t("ptmScorecamCategory", "Scorecam {{code}}", {
                                            code: cat.code,
                                          })
                                        : t("cmOpenScorecam", "Scorecam")}
                                    </button>
                                  </React.Fragment>
                                );
                              })}
                              {event.pdf_url ? (
                                <a
                                  className="comp-get-results"
                                  href={event.pdf_url}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <Download size={14} aria-hidden /> PDF
                                </a>
                              ) : null}
                            </div>

                            {isCoach ? (
                              <div className="comp-fltt-participants">
                                <h3>
                                  {t(
                                    "competitionClubParticipants",
                                    "Club participants"
                                  )}
                                  {shownParticipants.length
                                    ? ` (${shownParticipants.length})`
                                    : ""}
                                </h3>
                                {shownParticipants.length ? (
                                  <ul>
                                    {shownParticipants.map((p) => {
                                      const cat = allCats.find(
                                        (c) => c.category_id === p.category_id
                                      );
                                      const label = cat
                                        ? `${cat.start_time || ""} ${cat.code}`.trim()
                                        : p.category_code ||
                                          (p.category_id
                                            ? p.category_id
                                            : t(
                                                "competitionWholeEvent",
                                                "Whole event"
                                              ));
                                      return (
                                        <li key={p.rsvp_id}>
                                          <strong>{p.name}</strong>
                                          <span>
                                            {p.status === "pending"
                                              ? `${t("competitionMaybe", "Maybe")} · `
                                              : ""}
                                            {label}
                                          </span>
                                        </li>
                                      );
                                    })}
                                  </ul>
                                ) : (
                                  <p className="comp-muted">
                                    {t(
                                      "competitionNoParticipantsYet",
                                      "No players have notified participation yet."
                                    )}
                                  </p>
                                )}
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </article>
                    </li>
                  );
                })}
              </ul>
              </>
            ) : flttTournaments.length ? (
              <p className="comp-muted">
                {t(
                  "competitionNoScheduledTournaments",
                  "No upcoming tournaments — past results are listed above."
                )}
              </p>
            ) : (
              <div className="comp-empty">
                <div className="comp-empty__icon" aria-hidden>
                  <Medal size={28} strokeWidth={1.8} />
                </div>
                <h2>{t("competitionNoFlttTournaments", "No FLTT tournaments loaded")}</h2>
                <p>
                  {flttLoading
                    ? t("loading", "Loading…")
                    : t(
                        "competitionNoFlttTournamentsAuto",
                        "Tournaments load automatically from the FLTT calendar. Check back soon."
                      )}
                </p>
              </div>
            )
          ) : otherEvents.length ? (
            <ul className="comp-events__grid">
              {otherEvents.map((event) => {
                const Icon = kindIcon(event.kind);
                return (
                  <li key={event.competition_id}>
                    <article className={`comp-event-card kind-${event.kind}`}>
                      <span className="comp-event-card__icon" aria-hidden>
                        <Icon size={22} strokeWidth={1.8} />
                      </span>
                      <span className="comp-event-card__body">
                        <strong>{event.name}</strong>
                        <em>
                          {event.kind === "cup"
                            ? t("competitionKindCup", "Cup")
                            : t("competitionKindTournament", "Tournament")}
                          {event.category ? ` · ${event.category}` : ""}
                          {` · ${
                            event.format === "teams"
                              ? t("competitionFormatTeams", "Teams")
                              : t("competitionFormatIndividual", "Individual")
                          }`}
                        </em>
                      </span>
                      <span className={`pill pill--${event.status === "active" ? "home" : "away"}`}>
                        {event.status === "active"
                          ? t("competitionStatusActive", "Active")
                          : event.status === "draft"
                            ? t("competitionStatusDraft", "Draft")
                            : t("competitionStatusArchived", "Archived")}
                      </span>
                    </article>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="comp-empty">
              <div className="comp-empty__icon" aria-hidden>
                <Medal size={28} strokeWidth={1.8} />
              </div>
              <h2>{t("competitionNoTournaments", "No cups or tournaments yet")}</h2>
              <p>
                {isCoach
                  ? t(
                      "competitionNoTournamentsCoach",
                      "Add a cup or tournament from Competitions in the club admin."
                    )
                  : t(
                      "competitionNoTournamentsPlayer",
                      "Cups and other tournaments will appear here when your club adds them."
                    )}
              </p>
              {isAdmin ? (
                <Link className="comp-empty__cta" to="/Competitions">
                  {t("competitionOpenAdmin", "Open competitions")}
                </Link>
              ) : null}
            </div>
          )}

        </section>
        </>
      ) : null}

      {pickerOpen ? (
        <div className="comp-sheet" role="dialog" aria-modal="true" aria-labelledby="comp-sheet-title">
          <button
            type="button"
            className="comp-sheet__scrim"
            aria-label={t("close", "Close")}
            onClick={() => setPickerOpen(false)}
          />
          <div className="comp-sheet__panel">
            <div className="comp-sheet__grab" aria-hidden />
            <header>
              <h2 id="comp-sheet-title">{t("competitionPickTeam", "Choose a team")}</h2>
              <button type="button" onClick={() => setPickerOpen(false)} aria-label={t("close", "Close")}>
                <X size={18} />
              </button>
            </header>
            <label className="comp-search">
              <Search size={16} aria-hidden />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("competitionSearchTeams", "Search clubs or divisions")}
              />
            </label>
            {visibleClubTeams.length ? (
              <>
                <p className="comp-sheet__sec">{t("competitionClubTeams", "Club teams")}</p>
                <ul className="comp-sheet__list">
                  {visibleClubTeams.map((team) => (
                    <li key={team.league_team_id}>
                      <button type="button" onClick={() => onPickTeam(team)}>
                        <span className="comp-sheet__team">
                          <ClubCrest team={team} ownClubId={schedule?.club_id} />
                          <strong>{team.display_name}</strong>
                        </span>
                        <span className="comp-sheet__div">{team.division}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}
            <p className="comp-sheet__sec">{t("competitionOtherTeams", "Other teams")}</p>
            {directoryLoading && !otherTeams.length ? (
              <p className="comp-muted">{t("loading", "Loading…")}</p>
            ) : null}
            <ul className="comp-sheet__list">
              {otherTeams.slice(0, 80).map((team) => (
                <li key={team.league_team_id}>
                  <button type="button" onClick={() => onPickTeam(team, { foreign: true })}>
                    <span className="comp-sheet__team">
                      <ClubCrest team={team} ownClubId={schedule?.club_id} />
                      <strong>{team.display_name}</strong>
                    </span>
                    <span className="comp-sheet__div">{team.division}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
      </div>

      {clubCard ? (
        <Suspense fallback={null}>
          <ClubUsersModal
            isOpen
            onClose={() => setClubCard(null)}
            clubId={clubCard?.club_id}
            clubName={clubCard?.club_name}
            clubImage={clubCard?.club_image}
            clubData={clubCard}
          />
        </Suspense>
      ) : null}
    </main>
  );
}

function fold(s) {
  return String(s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function foldDiv(value) {
  return fold(value)
    .replace(/\bpromotion\b/g, "prom")
    .replace(/\bdistrict\b/g, "dis")
    .replace(/[-–]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function standingsTablesFromSchedule(schedule) {
  const raw = schedule?.standings || schedule?.season?.standings || [];
  return Array.isArray(raw) ? raw : [];
}

function standingsTableForTeam(tables, team) {
  if (!team) return null;
  const list = (Array.isArray(tables) ? tables : []).filter((t) => t?.rows?.length);
  const div = foldDiv(team.division);
  const name = fold(team.display_name);
  const named = list.filter((t) =>
    (t.rows || []).some((row) => fold(row.team_name) === name)
  );
  if (named.length === 1) return named[0];
  if (div) {
    const sameDiv = named.find((t) => foldDiv(t.division) === div);
    if (sameDiv) return sameDiv;
  }
  return named[0] || null;
}

function standingsRowForTeam(table, team) {
  if (!table || !team) return null;
  const name = fold(team.display_name);
  return (table.rows || []).find((row) => fold(row.team_name) === name) || null;
}

function seasonRoundLabel(season, t) {
  if (String(season?.round_code || "").startsWith("r1_")) {
    return season?.season_label || t("competitionRoundFirst", "First half");
  }
  const raw = `${season?.round_code || ""} ${season?.round_label || ""}`;
  const n = Number((raw.match(/(\d+)/) || [])[1] || 1);
  if (n >= 2) return t("competitionRoundReturn", "Second half");
  return t("competitionRoundFirst", "First half");
}
